/**
 * 워크플로우 노드 설정 모달 컴포넌트들
 */

import { Fragment, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import connectorApi from '../../api/connectors';
import emailConnectorApi from '../../api/email-connectors';
import type { EmailConnectorConfig } from '../../api/email-connectors';
import { EmailConnectorModal } from './EmailConnectorModal';
import { Badge, Button, Select, useToast, EmptyState, Notice } from '../ui';
import EditIcon from '../../assets/icons/common/edit.svg?react';
import CopyIcon from '../../assets/icons/common/copy.svg?react';
import PlusIcon from '../../assets/icons/common/plus.svg?react';
import UploadIcon from '../../assets/icons/common/upload.svg?react';
import CheckIcon from '../../assets/icons/common/check.svg?react';
import RefreshIcon from '../../assets/icons/common/refresh.svg?react';
import SettingIcon from '../../assets/icons/common/setting.svg?react';
import MailIcon from '../../assets/icons/dashboard/mail.svg?react';
import SendIcon from '../../assets/icons/common/send.svg?react';
import ListIcon from '../../assets/icons/common/list.svg?react';
import googleDriveLogo from '../../assets/icons/marketplace/google-drive.svg';
import gmailLogo from '../../assets/icons/marketplace/gmail.svg';
import '../../styles/workflow.css';

// 답변 전송 노드 설정
interface OutputNodeConfig {
  channel?: 'email' | 'slack' | 'teams' | 'webhook' | 'sms';
  emailTo?: string;
  emailSubject?: string;
  emailFormat?: 'text' | 'html';
  slackChannel?: string;
  slackFormat?: 'text' | 'block';
  teamsWebhook?: string;
  webhookUrl?: string;
  webhookMethod?: 'POST' | 'PUT';
  webhookFormat?: 'json' | 'text';
  smsTo?: string;
  messageTemplate?: string;
}

interface OutputConfigModalProps {
  config: OutputNodeConfig;
  onChange: (config: OutputNodeConfig) => void;
}

export function OutputConfigModal({ config, onChange }: OutputConfigModalProps) {
  return (
    <div className="form-grid">
      <div className="form-item">
        <label className='form-label'>전송 채널</label>
        <Select
          value={config.channel || 'email'}
          onChange={(e) => onChange({ ...config, channel: e.target.value as OutputNodeConfig['channel'] })}
        >
          <option value="email">이메일</option>
          <option value="slack">Slack</option>
          <option value="teams">Microsoft Teams</option>
          <option value="webhook">웹훅 (API 응답)</option>
          <option value="sms">SMS</option>
        </Select>
      </div>

      {/* 이메일 설정 */}
      {config.channel === 'email' && (
        <>
          <div className="form-item">
            <label className='form-label'>수신자 이메일</label>
            <input
              type="text"
              placeholder="{{trigger.from}} 또는 user@example.com"
              value={config.emailTo || ''}
              onChange={(e) => onChange({ ...config, emailTo: e.target.value })}
            />
            <small className="form-sublabel">
              변수 사용 가능: {'{{trigger.from}}'}, {'{{trigger.email}}'}
            </small>
          </div>
          <div className="form-item">
            <label className='form-label'>제목</label>
            <input
              type="text"
              placeholder="RE: {{trigger.subject}}"
              value={config.emailSubject || ''}
              onChange={(e) => onChange({ ...config, emailSubject: e.target.value })}
            />
          </div>
          <div className="form-item">
            <label className='form-label'>본문 형식</label>
            <Select
              value={config.emailFormat || 'html'}
              onChange={(e) => onChange({ ...config, emailFormat: e.target.value as 'text' | 'html' })}
            >
              <option value="html">HTML (서식 포함)</option>
              <option value="text">일반 텍스트</option>
            </Select>
          </div>
        </>
      )}

      {/* Slack 설정 */}
      {config.channel === 'slack' && (
        <>
          <div className="form-item">
            <label className='form-label'>채널/사용자</label>
            <input
              type="text"
              placeholder="#general 또는 @username"
              value={config.slackChannel || ''}
              onChange={(e) => onChange({ ...config, slackChannel: e.target.value })}
            />
          </div>
          <div className="form-item">
            <label className='form-label'>메시지 형식</label>
            <Select
              value={config.slackFormat || 'text'}
              onChange={(e) => onChange({ ...config, slackFormat: e.target.value as 'text' | 'block' })}
            >
              <option value="text">일반 텍스트</option>
              <option value="block">블록 형식 (리치 포맷)</option>
            </Select>
          </div>
        </>
      )}

      {/* Teams 설정 */}
      {config.channel === 'teams' && (
        <div className="form-item form-item--full">
          <label className='form-label'>Teams 웹훅 URL</label>
          <input
            type="text"
            placeholder="https://outlook.office.com/webhook/..."
            value={config.teamsWebhook || ''}
            onChange={(e) => onChange({ ...config, teamsWebhook: e.target.value })}
          />
        </div>
      )}

      {/* 웹훅 설정 */}
      {config.channel === 'webhook' && (
        <>
          <div className="form-item">
            <label className='form-label'>웹훅 URL</label>
            <input
              type="text"
              placeholder="https://api.example.com/callback"
              value={config.webhookUrl || ''}
              onChange={(e) => onChange({ ...config, webhookUrl: e.target.value })}
            />
          </div>
          <div className="form-item">
            <label className='form-label'>HTTP 메서드</label>
            <Select
              value={config.webhookMethod || 'POST'}
              onChange={(e) => onChange({ ...config, webhookMethod: e.target.value as 'POST' | 'PUT' })}
            >
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
            </Select>
          </div>
          <div className="form-item">
            <label className='form-label'>응답 형식</label>
            <Select
              value={config.webhookFormat || 'json'}
              onChange={(e) => onChange({ ...config, webhookFormat: e.target.value as 'json' | 'text' })}
            >
              <option value="json">JSON</option>
              <option value="text">텍스트</option>
            </Select>
          </div>
        </>
      )}

      {/* SMS 설정 */}
      {config.channel === 'sms' && (
        <div className="form-item form-item--full">
          <label className='form-label'>수신 전화번호</label>
          <input
            type="text"
            placeholder="010-1234-5678"
            value={config.smsTo || ''}
            onChange={(e) => onChange({ ...config, smsTo: e.target.value })}
          />
        </div>
      )}

      {/* 공통: 메시지 템플릿 */}
      <div className="form-item form-item--full">
        <label className='form-label'>메시지 템플릿<span className='optional'>(선택)</span></label>
        <textarea
          className="config-textarea"
          rows={3}
          placeholder="AI 응답을 그대로 전송하려면 비워두세요"
          value={config.messageTemplate || ''}
          onChange={(e) => onChange({ ...config, messageTemplate: e.target.value })}
        />
        <small className="form-sublabel">
          변수: {'{{response}}'} - AI 응답, {'{{query}}'} - 원본 질문
        </small>
      </div>
    </div>
  );
}


// 담당자 전송 노드 설정
interface NotifyNodeConfig {
  channel?: 'email' | 'slack' | 'teams' | 'sms';
  recipients?: string;
  slackChannel?: string;
  messageTemplate?: string;
  urgency?: 'normal' | 'important' | 'urgent';
  includeContext?: boolean;
}

interface NotifyConfigModalProps {
  config: NotifyNodeConfig;
  onChange: (config: NotifyNodeConfig) => void;
}

export function NotifyConfigModal({ config, onChange }: NotifyConfigModalProps) {
  return (
    <div className="form-grid form-grid--2">
      <div className="form-item">
        <label className='form-label'>알림 채널</label>
        <Select
          value={config.channel || 'slack'}
          onChange={(e) => onChange({ ...config, channel: e.target.value as NotifyNodeConfig['channel'] })}
        >
          <option value="slack">Slack</option>
          <option value="email">이메일</option>
          <option value="teams">Microsoft Teams</option>
          <option value="sms">SMS</option>
        </Select>
      </div>

      <div className="form-item">
        <label className='form-label'>긴급도</label>
        <Select
          value={config.urgency || 'normal'}
          onChange={(e) => onChange({ ...config, urgency: e.target.value as NotifyNodeConfig['urgency'] })}
        >
          <option value="normal">일반</option>
          <option value="important">중요</option>
          <option value="urgent">긴급</option>
        </Select>
      </div>

      {config.channel === 'slack' && (
        <div className="form-item form-item--full">
          <label className='form-label'>Slack 채널</label>
          <input
            type="text"
            placeholder="#support 또는 @username"
            value={config.slackChannel || ''}
            onChange={(e) => onChange({ ...config, slackChannel: e.target.value })}
          />
        </div>
      )}

      {(config.channel === 'email' || config.channel === 'sms') && (
        <div className="form-item form-item--full">
          <label className='form-label'>수신자 (쉼표로 구분)</label>
          <input
            type="text"
            placeholder={config.channel === 'email' ? 'admin@company.com, support@company.com' : '010-1234-5678, 010-9876-5432'}
            value={config.recipients || ''}
            onChange={(e) => onChange({ ...config, recipients: e.target.value })}
          />
        </div>
      )}

      <div className="form-item form-item--full">
        <label className='form-label'>메시지 템플릿</label>
        <textarea
          className="config-textarea"
          rows={3}
          placeholder="AI가 답변하지 못한 문의가 있습니다.&#10;질문: {{query}}"
          value={config.messageTemplate || ''}
          onChange={(e) => onChange({ ...config, messageTemplate: e.target.value })}
        />
      </div>

      <div className="form-item form-item--full">
        <label className='form-label'>대화 내용 포함</label>
        <Select
          value={config.includeContext ? 'true' : 'false'}
          onChange={(e) => onChange({ ...config, includeContext: e.target.value === 'true' })}
        >
          <option value="true">포함 (전체 대화 기록)</option>
          <option value="false">미포함 (메시지만)</option>
        </Select>
      </div>
    </div>
  );
}

// 메일 트리거 노드 설정
interface MailTriggerConfig {
  emailAddress?: string;
  subjectFilter?: string;
  senderFilter?: string;
}

interface MailTriggerConfigModalProps {
  config: MailTriggerConfig;
  onChange: (config: MailTriggerConfig) => void;
}

export function MailTriggerConfigModal({ config, onChange }: MailTriggerConfigModalProps) {
  return (
    <div className="form-grid">
      <div className="form-item">
        <label className='form-label'>수신 이메일 주소</label>
        <input
          type="text"
          placeholder="support@company.com"
          value={config.emailAddress || ''}
          onChange={(e) => onChange({ ...config, emailAddress: e.target.value })}
        />
        <small className="form-sublabel">
          이 주소로 메일이 오면 워크플로우가 시작됩니다.
        </small>
      </div>

      <div className="form-item">
        <label className='form-label'>제목 필터 (선택)</label>
        <input
          type="text"
          placeholder="[문의] 또는 비워두면 모든 메일"
          value={config.subjectFilter || ''}
          onChange={(e) => onChange({ ...config, subjectFilter: e.target.value })}
        />
      </div>

      <div className="form-item">
        <label className='form-label'>발신자 필터 (선택)</label>
        <input
          type="text"
          placeholder="@company.com 또는 비워두면 모든 발신자"
          value={config.senderFilter || ''}
          onChange={(e) => onChange({ ...config, senderFilter: e.target.value })}
        />
      </div>
    </div>
  );
}


// 스케줄 트리거 노드 설정
interface ScheduleTriggerConfig {
  scheduleType?: 'interval' | 'cron' | 'daily' | 'weekly' | 'hourly';
  interval?: number;
  cronExpression?: string;
  dailyTime?: string;
  dailyHour?: number;
  dailyMinute?: number;
  hourlyMinute?: number;
  dailySecond?: number;
  weeklyDay?: string;
  weeklyTime?: string;
  weeklyHour?: number;
  weeklyMinute?: number;
  weeklySecond?: number;
  timezone?: string;
}

interface ScheduleTriggerConfigModalProps {
  config: ScheduleTriggerConfig;
  onChange: (config: ScheduleTriggerConfig) => void;
}

export function ScheduleTriggerConfigModal({ config, onChange }: ScheduleTriggerConfigModalProps) {
  // scheduleType 미설정 시 'daily'로 초기화 (저장 시 기본값 보장)
  const safeConfig: ScheduleTriggerConfig = {
    scheduleType: 'daily',
    ...config,
  };

  return (
    <div className="form-grid">
      <div className="form-item">
        <label className='form-label'>스케줄 유형</label>
        <Select
          value={safeConfig.scheduleType}
          onChange={(e) => onChange({ ...safeConfig, scheduleType: e.target.value as ScheduleTriggerConfig['scheduleType'] })}
        >
          <option value="interval">일정 간격</option>
          <option value="hourly">매시 (정시 실행)</option>
          <option value="daily">매일</option>
          <option value="weekly">매주</option>
          <option value="cron">Cron 표현식</option>
        </Select>
      </div>

      {safeConfig.scheduleType === 'interval' && (
        <div className="form-item">
          <label className='form-label'>실행 간격 (분)</label>
          <input
            type="number"
            placeholder="60"
            value={safeConfig.interval || ''}
            onChange={(e) => onChange({ ...safeConfig, interval: parseInt(e.target.value) || undefined })}
          />
        </div>
      )}

      {safeConfig.scheduleType === 'hourly' && (
        <div className="form-item">
          <label className='form-label'>매시 몇 분에 실행</label>
          <div className="schedule-time-row">
            <div className="schedule-time-field">
              <Select
                value={safeConfig.hourlyMinute ?? 0}
                onChange={(e) => onChange({ ...safeConfig, hourlyMinute: parseInt(e.target.value) })}
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}분</option>
                ))}
              </Select>
              <span className="schedule-time-unit">분</span>
            </div>
          </div>
          <small className="form-sublabel">
            매시 {String(safeConfig.hourlyMinute ?? 0).padStart(2, '0')}분에 실행됩니다 (예: 01:00, 02:00, 03:00 ...)
          </small>
        </div>
      )}

      {(safeConfig.scheduleType === 'daily' || !safeConfig.scheduleType) && (
        <div className="form-item">
          <label className='form-label'>실행 시간</label>
          <div className="schedule-time-row">
            <div className="schedule-time-field">
              <Select
                value={safeConfig.dailyHour ?? 9}
                onChange={(e) => onChange({ ...safeConfig, dailyHour: parseInt(e.target.value) })}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                ))}
              </Select>
              <span className="">시</span>
            </div>
            <div className="schedule-time-field">
              <Select
                value={safeConfig.dailyMinute ?? 0}
                onChange={(e) => onChange({ ...safeConfig, dailyMinute: parseInt(e.target.value) })}
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                ))}
              </Select>
              <span className="">분</span>
            </div>
            <div className="schedule-time-field">
              <Select
                value={safeConfig.dailySecond ?? 0}
                onChange={(e) => onChange({ ...safeConfig, dailySecond: parseInt(e.target.value) })}
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                ))}
              </Select>
              <span className="">초</span>
            </div>
          </div>
          <small className="form-sublabel">
            매일 {String(safeConfig.dailyHour ?? 9).padStart(2,'0')}:{String(safeConfig.dailyMinute ?? 0).padStart(2,'0')}:{String(safeConfig.dailySecond ?? 0).padStart(2,'0')} 에 실행됩니다
          </small>
        </div>
      )}

      {safeConfig.scheduleType === 'weekly' && (
        <>
          <div className="form-item">
            <label className='form-label'>요일</label>
            <Select
              value={safeConfig.weeklyDay || 'monday'}
              onChange={(e) => onChange({ ...safeConfig, weeklyDay: e.target.value })}
            >
              <option value="monday">월요일</option>
              <option value="tuesday">화요일</option>
              <option value="wednesday">수요일</option>
              <option value="thursday">목요일</option>
              <option value="friday">금요일</option>
              <option value="saturday">토요일</option>
              <option value="sunday">일요일</option>
            </Select>
          </div>
          <div className="form-item">
            <label className='form-label'>실행 시간</label>
            <div className="schedule-time-row">
              <div className="schedule-time-field">
                <Select
                  value={safeConfig.weeklyHour ?? 9}
                  onChange={(e) => onChange({ ...safeConfig, weeklyHour: parseInt(e.target.value) })}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                  ))}
                </Select>
                <span className="">시</span>
              </div>
              <div className="schedule-time-field">
                <Select
                  value={safeConfig.weeklyMinute ?? 0}
                  onChange={(e) => onChange({ ...safeConfig, weeklyMinute: parseInt(e.target.value) })}
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                  ))}
                </Select>
              </div>
              <span className="">분</span>
              <div className="schedule-time-field">
                <Select
                  value={safeConfig.weeklySecond ?? 0}
                  onChange={(e) => onChange({ ...safeConfig, weeklySecond: parseInt(e.target.value) })}
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                  ))}
                </Select>
              </div>
              <span className="">초</span>
            </div>
            <small className="form-sublabel">
              매주 {safeConfig.weeklyDay || 'monday'} {String(safeConfig.weeklyHour ?? 9).padStart(2,'0')}:{String(safeConfig.weeklyMinute ?? 0).padStart(2,'0')}:{String(safeConfig.weeklySecond ?? 0).padStart(2,'0')} 에 실행됩니다
            </small>
          </div>
        </>
      )}

      {safeConfig.scheduleType === 'cron' && (
        <div className="form-item">
          <label className='form-label'>Cron 표현식</label>
          <input
            type="text"
            placeholder="0 9 * * *"
            value={safeConfig.cronExpression || ''}
            onChange={(e) => onChange({ ...safeConfig, cronExpression: e.target.value })}
          />
          <small className="form-sublabel">
            예: 0 9 * * * (매일 오전 9시)
          </small>
        </div>
      )}

      <div className="form-item">
        <label className='form-label'>시간대</label>
        <Select
          value={safeConfig.timezone || 'Asia/Seoul'}
          onChange={(e) => onChange({ ...safeConfig, timezone: e.target.value })}
        >
          <option value="Asia/Seoul">한국 (KST)</option>
          <option value="UTC">UTC</option>
          <option value="America/New_York">미국 동부 (EST)</option>
          <option value="Europe/London">영국 (GMT)</option>
        </Select>
      </div>
    </div>
  );
}


// 웹훅 트리거 노드 설정
interface WebhookTriggerConfig {
  webhookPath?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  authType?: 'none' | 'api_key' | 'bearer';
  authValue?: string;
}

interface WebhookTriggerConfigModalProps {
  config: WebhookTriggerConfig;
  onChange: (config: WebhookTriggerConfig) => void;
}

export function WebhookTriggerConfigModal({ config, onChange }: WebhookTriggerConfigModalProps) {
  return (
    <div className="form-grid">
      <div className="form-item">
        <label className='form-label'>웹훅 경로</label>
        <input
          type="text"
          placeholder="/webhook/my-workflow"
          value={config.webhookPath || ''}
          onChange={(e) => onChange({ ...config, webhookPath: e.target.value })}
        />
        <small className="form-sublabel">
          전체 URL: https://your-domain.com{config.webhookPath || '/webhook/...'}
        </small>
      </div>

      <div className="form-item">
        <label className='form-label'>허용 메서드</label>
        <Select
          value={config.method || 'POST'}
          onChange={(e) => onChange({ ...config, method: e.target.value as WebhookTriggerConfig['method'] })}
        >
          <option value="POST">POST</option>
          <option value="GET">GET</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
        </Select>
      </div>

      <div className="form-item">
        <label className='form-label'>인증 방식</label>
        <Select
          value={config.authType || 'none'}
          onChange={(e) => onChange({ ...config, authType: e.target.value as WebhookTriggerConfig['authType'] })}
        >
          <option value="none">없음</option>
          <option value="api_key">API Key (헤더)</option>
          <option value="bearer">Bearer Token</option>
        </Select>
      </div>

      {config.authType && config.authType !== 'none' && (
        <div className="form-item">
          <label className='form-label'>{config.authType === 'api_key' ? 'API Key' : 'Bearer Token'}</label>
          <input
            type="password"
            placeholder="인증 값 입력"
            value={config.authValue || ''}
            onChange={(e) => onChange({ ...config, authValue: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

// HTTP 요청 노드 설정
interface HttpCallConfig {
  url?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: string;
  body?: string;
  timeout?: number;
  responseType?: 'json' | 'text' | 'blob';
}

interface HttpCallConfigModalProps {
  config: HttpCallConfig;
  onChange: (config: HttpCallConfig) => void;
}

export function HttpCallConfigModal({ config, onChange }: HttpCallConfigModalProps) {
  return (
    <div className="form-grid form-grid--2">
      <div className="form-item form-item--full">
        <label className='form-label'>URL</label>
        <input
          type="text"
          placeholder="https://api.example.com/endpoint"
          value={config.url || ''}
          onChange={(e) => onChange({ ...config, url: e.target.value })}
        />
      </div>

      <div className="form-item">
        <label className='form-label'>메서드</label>
        <Select
          value={config.method || 'GET'}
          onChange={(e) => onChange({ ...config, method: e.target.value as HttpCallConfig['method'] })}
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </Select>
      </div>

      <div className="form-item">
        <label className='form-label'>타임아웃 (초)</label>
        <input
          type="number"
          placeholder="30"
          value={config.timeout || ''}
          onChange={(e) => onChange({ ...config, timeout: parseInt(e.target.value) || undefined })}
        />
      </div>

      <div className="form-item form-item--full">
        <label className='form-label'>헤더 (JSON)</label>
        <textarea
          className="config-textarea"
          rows={2}
          placeholder='{"Authorization": "Bearer xxx", "Content-Type": "application/json"}'
          value={config.headers || ''}
          onChange={(e) => onChange({ ...config, headers: e.target.value })}
        />
      </div>

      {(config.method === 'POST' || config.method === 'PUT' || config.method === 'PATCH') && (
        <div className="form-item form-item--full">
          <label className='form-label'>요청 본문</label>
          <textarea
            className="config-textarea"
            rows={3}
            placeholder='{"key": "value"}'
            value={config.body || ''}
            onChange={(e) => onChange({ ...config, body: e.target.value })}
          />
        </div>
      )}

      <div className="form-item">
        <label className='form-label'>응답 형식</label>
        <Select
          value={config.responseType || 'json'}
          onChange={(e) => onChange({ ...config, responseType: e.target.value as HttpCallConfig['responseType'] })}
        >
          <option value="json">JSON</option>
          <option value="text">텍스트</option>
          <option value="blob">바이너리</option>
        </Select>
      </div>
    </div>
  );
}


// 이메일 발송 노드 설정
interface EmailSendConfig {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  bodyTemplate?: string;
  bodyFormat?: 'text' | 'html';
}

interface EmailSendConfigModalProps {
  config: EmailSendConfig;
  onChange: (config: EmailSendConfig) => void;
}

export function EmailSendConfigModal({ config, onChange }: EmailSendConfigModalProps) {
  return (
    <div className="form-grid form-grid--2">
      <div className="form-item">
        <label className='form-label'>받는 사람</label>
        <input
          type="text"
          placeholder="user@example.com, user2@example.com"
          value={config.to || ''}
          onChange={(e) => onChange({ ...config, to: e.target.value })}
        />
      </div>

      <div className="form-item">
        <label className='form-label'>참조 (CC)</label>
        <input
          type="text"
          placeholder="cc@example.com"
          value={config.cc || ''}
          onChange={(e) => onChange({ ...config, cc: e.target.value })}
        />
      </div>

      <div className="form-item form-item--full">
        <label className='form-label'>제목</label>
        <input
          type="text"
          placeholder="[알림] {{subject}}"
          value={config.subject || ''}
          onChange={(e) => onChange({ ...config, subject: e.target.value })}
        />
      </div>

      <div className="form-item">
        <label className='form-label'>본문 형식</label>
        <Select
          value={config.bodyFormat || 'html'}
          onChange={(e) => onChange({ ...config, bodyFormat: e.target.value as 'text' | 'html' })}
        >
          <option value="html">HTML</option>
          <option value="text">일반 텍스트</option>
        </Select>
      </div>

      <div className="form-item form-item--full">
        <label className='form-label'>본문 템플릿</label>
        <textarea
          className="config-textarea"
          rows={4}
          placeholder="안녕하세요,&#10;&#10;{{content}}&#10;&#10;감사합니다."
          value={config.bodyTemplate || ''}
          onChange={(e) => onChange({ ...config, bodyTemplate: e.target.value })}
        />
        <small className="form-sublabel">
          변수: {'{{content}}'}, {'{{query}}'}, {'{{response}}'}
        </small>
      </div>
    </div>
  );
}

// Google Drive 노드 설정
interface GoogleDriveConfig {
  operation?: 'watch_new' | 'watch_modified' | 'read' | 'upload' | 'list';
  folderId?: string;
  fileFilter?: string;
  fileId?: string;
  uploadFileName?: string;
  uploadFile?: File; // 실제 업로드할 파일 객체
  // OAuth 연결
  clientId?: string;
  clientSecret?: string;
  connected?: boolean;
  connectedEmail?: string;
  setupStep?: number;
}

// ── 기존 더미 모달 — 아래 OAuth 단계 모달로 대체됨 ──
/*
export function GoogleDriveConfigModal_OLD
  return (
    <div className="form-grid">
      <div className="form-item node-connect-field">
        <Button color="gray" appearance="outline" className="node-connect-btn">🔗 Google 계정 연결</Button>
      </div>
    </div>
  );
}
*/


// Slack 노드 설정
interface SlackChannelOption {
  id: string;
  name: string;
  isPrivate: boolean;
}

interface SlackConfig {
  operation?: 'send' | 'read';
  channel?: string;          // 채널 ID
  message?: string;          // 발송 메시지 (비우면 앞 노드 결과 사용)
  limit?: number;            // 읽기 개수
  // 연결 상태
  setupStep?: number;
  botToken?: string;
  connected?: boolean;
  teamName?: string;
}

interface SlackConfigModalProps {
  config: SlackConfig;
  onChange: (config: SlackConfig) => void;
}

const SLACK_STEPS = ['Slack 앱 만들기', 'Bot Token 입력', '작업 설정'];

const getSlackStep = (config: SlackConfig) =>
  config.setupStep ?? (config.connected && config.botToken ? 3 : 1);

/** 스텝 인디케이터 — 모달 subHeader 로 사용 */
export function SlackSteps({ config }: { config: SlackConfig }) {
  return <NodeSetupSteps labels={SLACK_STEPS} step={getSlackStep(config)} />;
}

export function SlackConfigModal({ config, onChange }: SlackConfigModalProps) {
  const step = getSlackStep(config);
  const setStep = (s: number) => onChange({ ...config, setupStep: s });
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');
  const [channels, setChannels] = useState<SlackChannelOption[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  const handleTestConnection = async () => {
    if (!config.botToken) return;
    setTesting(true);
    setTestError('');
    try {
      const data = await connectorApi.testSlackToken(config.botToken);
      onChange({ ...config, connected: true, teamName: data.team, setupStep: 3 });
    } catch (err: unknown) {
      setTestError((err as Error).message || '연결 실패. 토큰을 확인하세요.');
    } finally {
      setTesting(false);
    }
  };

  const loadChannels = async () => {
    setLoadingChannels(true);
    try {
      const list = await connectorApi.getSlackChannels();
      setChannels(list);
    } catch {
      /* 조회 실패 시 수동 입력으로 폴백 */
    } finally {
      setLoadingChannels(false);
    }
  };

  // 3단계 진입 시 채널 목록 자동 로드
  useEffect(() => {
    if (step === 3 && config.connected && channels.length === 0) {
      loadChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, config.connected]);

  return (
    <div className="gmail-setup">

      {/* ── Step 1: Slack 앱 생성 가이드 ── */}
      {step === 1 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Slack 앱 만들기</div>
            <p className="node-guide-desc">Slack API를 사용하려면 앱을 먼저 만들어야 합니다. 워크스페이스당 한 번만 하면 됩니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. 앱 생성</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-1</span>
              <div>
                <strong>콘솔 로그인</strong>
                <p>
                  <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer">api.slack.com/apps</a> 접속 후 Slack 계정 로그인
                </p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-2</span>
              <div>
                <strong>새 앱 만들기</strong>
                <p>Create New App → From scratch</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-3</span>
              <div>
                <strong>앱 정보 입력</strong>
                <p>앱 이름 입력 (예: <code className="gmail-code">AgentBuilder</code>) → 사용할 워크스페이스 선택 → Create App</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. 권한 설정 및 설치</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div>
                <strong>Bot Token 권한 추가</strong>
                <p>왼쪽 메뉴 OAuth &amp; Permissions → Scopes → Bot Token Scopes에서 아래 4개 추가</p>
                <ul className="node-scope-list">
                  <li><code className="gmail-code">chat:write</code> 메시지 발송</li>
                  <li><code className="gmail-code">channels:read</code> 채널 목록</li>
                  <li><code className="gmail-code">channels:history</code> 메시지 읽기</li>
                </ul>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>워크스페이스에 설치</strong>
                <p>같은 화면 상단 Install to Workspace → 허용</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-3</span>
              <div>
                <strong>Bot Token 복사</strong>
                <p>설치 후 표시되는 Bot User OAuth Token 복사 · <code className="gmail-code">xoxb-</code>로 시작하는 토큰입니다</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 1 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" onClick={() => setStep(2)}>다음</Button>
        </div>
      )}

      {/* ── Step 2: 토큰 입력 + 연결 테스트 ── */}
      {step === 2 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Bot Token 입력</div>
            <p className="node-guide-desc">1단계에서 복사한 토큰을 붙여넣고 연결을 테스트하세요.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">Bot User OAuth Token <span className="required">*</span></div>
            <div className='form-item'>
              <div className="input">
                <input
                  type="password"
                  className="input-control"
                  placeholder="xoxb-xxxxxxxxxxxx-xxxxxxxxxxxx-xxxxxxxxxxxxxxxxxxxxxxxx"
                  value={config.botToken || ''}
                  onChange={(e) => onChange({ ...config, botToken: e.target.value, connected: false })}
                />
              </div>
              <small className="form-sublabel">토큰은 암호화되어 서버에 저장됩니다.</small>
            </div>
          </div>

          {testError && <Notice color="error">{testError}</Notice>}
        </div>
      )}
      {step === 2 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(1)}>이전</Button>
          <Button
            color="primary"
            disabled={!config.botToken || testing}
            onClick={handleTestConnection}
          >
            {testing ? '연결 확인 중...' : '연결 테스트'}
          </Button>
        </div>
      )}

      {/* ── Step 3: 채널 공유 가이드 + 작업 설정 ── */}
      {step === 3 && (
        <div className="bedrock-guide">
          {/* 연결된 워크스페이스 */}
          {config.connected && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">연결된 워크스페이스</div>
              <div className="node-account-card bedrock-guide-step">
                <div className="node-account-info">
                  <span className="node-account-icon" />
                  <div className="node-account-text">
                    <strong>{config.teamName || 'Slack 워크스페이스'}</strong>
                    <span>Slack 앱이 연결되어 있습니다</span>
                  </div>
                </div>
                <div className="node-account-actions">
                  <Button color="gray" appearance="outline" size="sm" onClick={() => onChange({ ...config, connected: false, botToken: '', setupStep: 2 })}>
                    <RefreshIcon /> 토큰 재입력
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 채널 초대 안내 */}
          <Notice color="warning" title="봇을 사용할 채널에 초대해야 합니다">
            <ul>
              <li>Slack에서 메시지를 보낼 <strong>채널</strong>을 엽니다</li>
              <li>채널 이름 클릭 → <strong>통합</strong> 탭 → <strong>앱 추가</strong></li>
              <li>또는 메시지창에 <code className="gmail-code">/invite @앱이름</code> 입력</li>
              <li>비공개 채널은 반드시 봇을 초대해야 메시지가 전송됩니다</li>
            </ul>
          </Notice>

          {/* 작업 유형 */}
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">작업 유형</div>
            <Select
              value={config.operation || 'send'}
              onChange={(e) => onChange({ ...config, operation: e.target.value as SlackConfig['operation'] })}
            >
              <option value="send">메시지 보내기</option>
              <option value="read">메시지 읽기</option>
            </Select>
          </div>

          {/* 채널 선택 — 드롭다운 (목록 로드 성공 시) + 새로고침 */}
          <div className="bedrock-guide-section">
            <div className="ec-node-section-head">
              <div className="bedrock-guide-title">채널</div>
              <Button color="gray" appearance="outline" size="sm" onClick={loadChannels} disabled={loadingChannels}>
                <RefreshIcon /> {loadingChannels ? '불러오는 중...' : '채널 목록 새로고침'}
              </Button>
            </div>
            <div className='form-item'>
              {channels.length > 0 ? (
                <Select
                  value={config.channel || ''}
                  onChange={(e) => onChange({ ...config, channel: e.target.value })}
                >
                  <option value="">채널을 선택하세요...</option>
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.isPrivate ? '🔒 ' : '# '}{c.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="input">
                  <input
                    type="text"
                    className="input-control"
                    placeholder="채널 ID (예: C01234567)"
                    value={config.channel || ''}
                    onChange={(e) => onChange({ ...config, channel: e.target.value })}
                  />
                </div>
              )}
              <small className="form-sublabel">
                {channels.length > 0 ? '봇이 초대된 채널만 발송됩니다.' : '채널 목록을 불러오면 드롭다운으로 선택할 수 있습니다.'}
              </small>
            </div>
          </div>

          {/* 발송: 메시지 입력 */}
          {config.operation !== 'read' && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title form-label mb-0">메시지<span className='optional'>(선택)</span></div>
              <div className='form-item'>
                <textarea
                  className="config-textarea"
                  rows={3}
                  placeholder="보낼 메시지를 입력하세요. 비워두면 앞 노드의 결과가 전송됩니다."
                  value={config.message || ''}
                  onChange={(e) => onChange({ ...config, message: e.target.value })}
                />
                <small className="form-sublabel">
                  {'{{trigger.query}}'} 같은 변수를 쓸 수 있습니다.
                </small>
              </div>
            </div>
          )}

          {/* 읽기: 개수 */}
          {config.operation === 'read' && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">읽을 메시지 개수</div>
              <div className="input">
                <input
                  type="number"
                  className="input-control"
                  min={1}
                  max={100}
                  placeholder="20"
                  value={config.limit ?? 20}
                  onChange={(e) => onChange({ ...config, limit: parseInt(e.target.value) || 20 })}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Jira 노드 설정
interface JiraConfig {
  operation?: 'create' | 'update' | 'search' | 'transition';
  projectKey?: string;
  issueType?: string;
  summary?: string;
  description?: string;
  assignee?: string;
  priority?: string;
}

interface JiraConfigModalProps {
  config: JiraConfig;
  onChange: (config: JiraConfig) => void;
}

export function JiraConfigModal({ config, onChange }: JiraConfigModalProps) {
  return (
    <div className="form-grid">
      <div className="form-item">
        <label className='form-label'>작업 유형</label>
        <Select
          value={config.operation || 'create'}
          onChange={(e) => onChange({ ...config, operation: e.target.value as JiraConfig['operation'] })}
        >
          <option value="create">이슈 생성</option>
          <option value="update">이슈 수정</option>
          <option value="transition">상태 변경</option>
          <option value="search">이슈 검색</option>
        </Select>
      </div>

      <div className="form-item">
        <label className='form-label'>프로젝트 키</label>
        <input
          type="text"
          placeholder="PROJ"
          value={config.projectKey || ''}
          onChange={(e) => onChange({ ...config, projectKey: e.target.value })}
        />
      </div>

      {config.operation === 'create' && (
        <>
          <div className="form-item">
            <label className='form-label'>이슈 유형</label>
            <Select
              value={config.issueType || 'Task'}
              onChange={(e) => onChange({ ...config, issueType: e.target.value })}
            >
              <option value="Task">Task</option>
              <option value="Bug">Bug</option>
              <option value="Story">Story</option>
              <option value="Epic">Epic</option>
            </Select>
          </div>

          <div className="form-item">
            <label className='form-label'>우선순위</label>
            <Select
              value={config.priority || 'Medium'}
              onChange={(e) => onChange({ ...config, priority: e.target.value })}
            >
              <option value="Highest">Highest</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
              <option value="Lowest">Lowest</option>
            </Select>
          </div>

          <div className="form-item">
            <label className='form-label'>제목 템플릿</label>
            <input
              type="text"
              placeholder="[자동 생성] {{query}}"
              value={config.summary || ''}
              onChange={(e) => onChange({ ...config, summary: e.target.value })}
            />
          </div>

          <div className="form-item">
            <label className='form-label'>담당자</label>
            <input
              type="text"
              placeholder="자동 배정 시 비워두세요"
              value={config.assignee || ''}
              onChange={(e) => onChange({ ...config, assignee: e.target.value })}
            />
          </div>
        </>
      )}

      <div className="form-item node-connect-field">
        <Button color="gray" appearance="outline" className="node-connect-btn">
          🔗 Jira 계정 연결
        </Button>
      </div>
    </div>
  );
}

// Gmail 노드 설정 (OAuth 방식)
interface GmailConfig {
  // 연결 설정
  clientId?: string;
  clientSecret?: string;
  connected?: boolean;
  connectedEmail?: string;
  // 작업 설정
  operation?: 'read' | 'send' | 'search' | 'reply' | 'label';
  query?: string;
  maxResults?: number;
  to?: string;
  subject?: string;
  labelFilter?: string;
  labelAction?: string;
  // 설정 단계
  setupStep?: number;
}

interface GmailConfigModalProps {
  config: GmailConfig;
  onChange: (config: GmailConfig) => void;
}

const GMAIL_STEPS = ['프로젝트 설정', 'OAuth 클라이언트', '계정 연결', '작업 설정'];
const GMAIL_REDIRECT_URI = 'http://localhost:3002/api/auth/gmail/callback';

const getGmailStep = (config: GmailConfig) =>
  config.setupStep ?? (config.connected && config.clientId ? 4 : 1);

/** 스텝 인디케이터 — 모달 subHeader 로 사용 */
export function GmailSteps({ config }: { config: GmailConfig }) {
  return <NodeSetupSteps labels={GMAIL_STEPS} step={getGmailStep(config)} />;
}

export function GmailConfigModal({ config, onChange }: GmailConfigModalProps) {
  const step = getGmailStep(config);
  const setStep = (s: number) => onChange({ ...config, setupStep: s });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const { showToast } = useToast();

  // 처음 마운트될 때 기존 설정 로드 — 노드에 clientId가 있는 경우만 실행
  useEffect(() => {
    if (!config.clientId) return; // 새 노드라면 스킵
    connectorApi.getGmailConfig().then(saved => {
      if (saved) {
        onChange({
          ...config,
          clientId: saved.clientId,
          connected: saved.connected,
          connectedEmail: saved.connectedEmail,
          setupStep: saved.connected ? 4 : (saved.clientId ? 3 : config.setupStep ?? 1),
        });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="gmail-setup">

      {/* ── Step 1: Google Cloud 설정 가이드 ── */}
      {step === 1 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Google Cloud 프로젝트 설정</div>
            <p className="node-guide-desc">Gmail API를 사용하려면 Google Cloud에서 앱을 등록해야 합니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. 프로젝트 만들기</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-1</span>
              <div>
                <strong>콘솔 로그인</strong>
                <p>
                  <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a> 접속 후 Google 계정 로그인
                </p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-2</span>
              <div>
                <strong>새 프로젝트 선택</strong>
                <p>상단 프로젝트 이름 → 새 프로젝트 · 기존 프로젝트가 있으면 그대로 사용 가능</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. Gmail API 사용 설정</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div>
                <strong>API 및 서비스 열기</strong>
                <p>왼쪽 메뉴 또는 검색창 → API 및 서비스</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>API 검색 후 사용 설정</strong>
                <p>라이브러리 → 검색창에 <code className="gmail-code">Gmail API</code> 입력 → 사용 설정</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">3. OAuth 동의 화면 구성</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-1</span>
              <div>
                <strong>OAuth 동의 화면 열기</strong>
                <p>왼쪽 메뉴 → OAuth 동의 화면 · "Google 계정 유형" 화면으로 이동되면 왼쪽 메뉴에서 다시 선택</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-2</span>
              <div>
                <strong>앱 정보 입력</strong>
                <p>대상자 → 앱 이름 · 이메일 입력</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-3</span>
              <div>
                <strong>권한 범위 추가</strong>
                <p>앱 → 범위 → 범위 추가 → <code className="gmail-code">gmail</code> 검색 → 아래 범위 선택</p>
                <ul className="node-scope-list">
                  <li><code className="gmail-code">gmail.readonly</code></li>
                  <li><code className="gmail-code">gmail.modify</code></li>
                </ul>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-4</span>
              <div>
                <strong>테스트 사용자 추가</strong>
                <p>테스트 사용자 (또는 API 및 서비스의 OAuth 동의 화면) → 사용자 추가 → 본인 Gmail 주소 입력</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 1 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" onClick={() => setStep(2)}>다음</Button>
        </div>
      )}

      {/* ── Step 2: Client ID / Secret 입력 ── */}
      {step === 2 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">OAuth 클라이언트 ID 생성</div>
            <p className="node-guide-desc">Google 계정 연결에 사용할 OAuth 클라이언트를 만들고 발급된 값을 입력합니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. OAuth 클라이언트 만들기</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-1</span>
              <div>
                <strong>클라이언트 만들기 시작</strong>
                <p>왼쪽 메뉴 → 사용자 인증 정보 → 상단 사용자 인증 정보 만들기 · "Google 계정 유형" 화면이면 왼쪽 메뉴에서 사용자 인증 정보 선택</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-2</span>
              <div>
                <strong>애플리케이션 정보 입력</strong>
                <p>유형: 웹 애플리케이션 · 이름: 자유 입력 (예: <code className="gmail-code">AgentBuilder</code>)</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-3</span>
              <div className="node-guide-step-body">
                <strong>승인된 리디렉션 URI 추가</strong>
                <p>승인된 리디렉션 URI → URI 추가 → 아래 값을 복사하여 붙여넣기</p>
                <div className="bedrock-ext-row node-copy-row">
                  <input className="input-control" type="text" value={GMAIL_REDIRECT_URI} readOnly />
                  <button
                    type="button"
                    className="bedrock-copy-btn node-copy-btn"
                    title="복사"
                    onClick={() => { navigator.clipboard.writeText(GMAIL_REDIRECT_URI); showToast('success', '리디렉션 URI 복사됨'); }}
                  ><CopyIcon /></button>
                </div>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-4</span>
              <div>
                <strong>만들기 후 값 복사</strong>
                <p>만들기 → 팝업에 표시되는 클라이언트 ID · 클라이언트 보안 비밀번호 복사</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. 클라이언트 ID 입력 <span className="required">*</span></div>
            <div className="input">
              <input type="text" className="input-control"
                placeholder="123456789-xxx.apps.googleusercontent.com"
                value={config.clientId || ''}
                onChange={(e) => onChange({ ...config, clientId: e.target.value })}
              />
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">3. 클라이언트 보안 비밀번호 입력 <span className="required">*</span></div>
            <div className="input">
              <input type="password" className="input-control"
                placeholder="GOCSPX-xxxxxxxxxx"
                value={config.clientSecret || ''}
                onChange={(e) => onChange({ ...config, clientSecret: e.target.value })}
              />
            </div>
            {saveError && <div className="node-save-error">{saveError}</div>}
          </div>
        </div>
      )}
      {step === 2 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(1)}>이전</Button>
          <Button
            color="primary"
            disabled={!config.clientId || !config.clientSecret || saving}
            onClick={async () => {
              setSaving(true);
              setSaveError('');
              try {
                await connectorApi.saveGmailConfig(config.clientId!, config.clientSecret!);
                setStep(3);
              } catch {
                setSaveError('저장 실패. 다시 시도해주세요.');
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? '저장 중...' : '저장 후 다음'}
          </Button>
        </div>
      )}

      {/* ── Step 3: Google 계정 연결 ── */}
      {step === 3 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Google 계정 연결</div>
            <p className="node-guide-desc">Gmail에 접근할 계정으로 로그인하여 아래 권한을 허용합니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. 허용되는 권한</div>
            <div className="node-permission-list">
              <div className="node-permission-item"><MailIcon />메일 읽기</div>
              <div className="node-permission-item"><SendIcon />메일 전송</div>
              <div className="node-permission-item"><ListIcon />라벨 관리</div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. 계정 연결</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div className="node-guide-step-body">
                <strong>Google 로그인</strong>
                <p>아래 버튼을 클릭하면 Google 로그인 창이 열립니다. Gmail에 접근할 계정으로 로그인해주세요.</p>
                <button
                  type="button"
                  className="btn-google node-oauth-btn"
                  onClick={async () => {
                    try {
                      const url = await connectorApi.getGmailAuthUrl();
                      const popup = window.open(url, 'gmail-auth', 'width=500,height=600');
                      // 팝업에서 메시지 수신
                      const handler = (e: MessageEvent) => {
                        if (e.data?.type === 'GMAIL_CONNECTED') {
                          window.removeEventListener('message', handler);
                          popup?.close();
                          onChange({ ...config, connected: true, connectedEmail: e.data.email, setupStep: 4 });
                        }
                      };
                      window.addEventListener('message', handler);
                    } catch {
                      alert('인증 URL을 가져오는데 실패했습니다. Client ID/Secret을 확인하세요.');
                    }
                  }}
                >
                  <img src={gmailLogo} alt="" /> Google 계정으로 연결
                </button>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>연결 확인</strong>
                <p>로그인 완료 후 아래 연결 확인 버튼을 눌러주세요</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 3 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(2)}>이전</Button>
          <Button
            color="primary"
            onClick={async () => {
              // 현재 연결 상태 확인
              try {
                const saved = await connectorApi.getGmailConfig();
                if (saved?.connected) {
                  onChange({ ...config, connected: true, connectedEmail: saved.connectedEmail, setupStep: 4 });
                } else {
                  alert('아직 연결되지 않았습니다. Google 계정 연결 버튼을 먼저 클릭해주세요.');
                }
              } catch {
                alert('연결 상태 확인 실패. 다시 시도해주세요.');
              }
            }}
          >
            연결 확인
          </Button>
        </div>
      )}

      {/* ── Step 4: 작업 설정 ── */}
      {step === 4 && (
        <div className="bedrock-guide">
          {/* 연결된 계정 */}
          {config.connectedEmail && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">연결된 계정</div>
              <div className="node-account-card bedrock-guide-step">
                <div className="node-account-info">
                  <span className="node-account-icon" />
                  <div className="node-account-text">
                    <strong>{config.connectedEmail}</strong>
                    <span>Gmail 계정이 연결되어 있습니다</span>
                  </div>
                </div>
                <div className="node-account-actions">
                  <Button color="gray" appearance="outline" size="sm" onClick={async () => {
                    await connectorApi.disconnectGmail().catch(() => {});
                    onChange({ ...config, connected: false, connectedEmail: undefined, setupStep: 3 });
                  }}>
                    <RefreshIcon /> 계정 재연결
                  </Button>
                  <Button color="gray" appearance="outline" size="sm" onClick={() => setStep(2)}>
                    <SettingIcon /> Client ID 재설정
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">작업 유형</div>
            <Select
              value={config.operation || 'send'}
              onChange={(e) => onChange({ ...config, operation: e.target.value as GmailConfig['operation'] })}
            >
              <option value="send">메일 발송</option>
              <option value="read">메일 읽기</option>
              <option value="search">메일 검색</option>
              <option value="reply">메일 답장</option>
              <option value="label">라벨 관리</option>
            </Select>
          </div>

          {(config.operation === 'send' || !config.operation) && (
            <>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">수신자</div>
                <div className="input">
                  <input type="text" className="input-control"
                    placeholder="recipient@example.com"
                    value={config.to || ''}
                    onChange={(e) => onChange({ ...config, to: e.target.value })}
                  />
                </div>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">제목</div>
                <div className="input">
                  <input type="text" className="input-control"
                    placeholder="메일 제목 (변수 사용 가능: {{변수명}})"
                    value={config.subject || ''}
                    onChange={(e) => onChange({ ...config, subject: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}

          {(config.operation === 'read' || config.operation === 'search') && (
            <>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">검색 쿼리</div>
                <div className="input">
                  <input type="text" className="input-control"
                    placeholder="is:unread from:boss@company.com"
                    value={config.query || ''}
                    onChange={(e) => onChange({ ...config, query: e.target.value })}
                  />
                </div>
                <small className="form-sublabel">
                  Gmail 검색 문법 사용 가능 (from:, to:, subject:, is:unread 등)
                </small>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">최대 조회 수</div>
                <Select
                  value={config.maxResults || 10}
                  onChange={(e) => onChange({ ...config, maxResults: parseInt(e.target.value) })}
                >
                  <option value={5}>5개</option>
                  <option value={10}>10개 (기본)</option>
                  <option value={20}>20개</option>
                  <option value={50}>50개</option>
                </Select>
              </div>
            </>
          )}

          {config.operation === 'reply' && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">답장 조건</div>
              <div className="input">
                <input type="text" className="input-control"
                  placeholder="is:unread subject:문의"
                  value={config.query || ''}
                  onChange={(e) => onChange({ ...config, query: e.target.value })}
                />
              </div>
            </div>
          )}

          {config.operation === 'label' && (
            <>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">라벨 작업</div>
                <Select
                  value={config.labelAction || 'add'}
                  onChange={(e) => onChange({ ...config, labelAction: e.target.value })}
                >
                  <option value="add">라벨 추가</option>
                  <option value="remove">라벨 제거</option>
                  <option value="list">라벨 목록 조회</option>
                </Select>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">라벨명</div>
                <div className="input">
                  <input type="text" className="input-control"
                    placeholder="라벨명 (예: 처리완료, 중요)"
                    value={config.labelFilter || ''}
                    onChange={(e) => onChange({ ...config, labelFilter: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
// ── 네이버 메일 노드 설정 ────────────────────────────────────────────────────

// ── 이메일 커넥터 노드 설정 (SMTP/IMAP 방식) ────────────────────────────────────────────────────

interface EmailConnectorNodeConfig {
  connectorId?: string;
  connectorName?: string;
  operation?: 'send' | 'read' | 'reply';
  query?: string;
  maxResults?: number;
  to?: string;
  subject?: string;
}

interface EmailConnectorNodeModalProps {
  config: EmailConnectorNodeConfig;
  onChange: (config: EmailConnectorNodeConfig) => void;
  showErrors?: boolean;
}

export function EmailConnectorNodeModal({ config, onChange, showErrors }: EmailConnectorNodeModalProps) {
  const [connectors, setConnectors] = useState<EmailConnectorConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editConnectorId, setEditConnectorId] = useState<string | undefined>(undefined);
  const { showToast } = useToast();

  const loadConnectors = () => {
    emailConnectorApi.list()
      .then(setConnectors)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadConnectors(); }, []);

  const openEdit = (connectorId: string) => {
    setEditConnectorId(connectorId);
    setAddModalOpen(true);
  };

  const openAdd = () => {
    setEditConnectorId(undefined);
    setAddModalOpen(true);
  };

  const handleDelete = async (connectorId: string) => {
    try {
      await emailConnectorApi.delete(connectorId);
      showToast('success', '커넥터가 삭제되었습니다.');
      if (config.connectorId === connectorId) {
        onChange({ ...config, connectorId: undefined, connectorName: undefined });
      }
      loadConnectors();
    } catch {
      showToast('error', '삭제에 실패했습니다.');
    }
  };

  return (
    <>
      {/* 이메일 커넥터 추가/편집 팝업 */}
      {addModalOpen && (
        <EmailConnectorModal
          isOpen={addModalOpen}
          connectorId={editConnectorId}
          onClose={() => { setAddModalOpen(false); setEditConnectorId(undefined); }}
          onSaved={(saved) => {
            setAddModalOpen(false);
            setEditConnectorId(undefined);
            setLoading(true);
            setConnectors([]);
            emailConnectorApi.list()
              .then(list => {
                setConnectors(list);
                if (saved?.connectorId) {
                  onChange({ ...config, connectorId: saved.connectorId, connectorName: saved.name });
                }
              })
              .catch(() => {})
              .finally(() => setLoading(false));
          }}
        />
      )}

      <div className="bedrock-guide">
      {loading ? (
        <div className="ec-node-loading">로딩 중...</div>
      ) : connectors.length === 0 ? (
        <EmptyState
          illust='no-data'
          title='등록된 이메일 커넥터가 없습니다.'
          size='lg'
          action={
            <Button color="primary" onClick={openAdd}>
              + 이메일 커넥터 추가
            </Button>
          }
        />
      ) : (
        <div className="bedrock-guide-section">
          <div className="ec-node-section-head">
            <div className="bedrock-guide-title">이메일 커넥터 선택</div>
            <Button color="primary" appearance="outline" size="sm" onClick={openAdd} className='ec-node-add-btn'>
              + 새 커넥터
            </Button>
          </div>
          <div className="ec-node-card-list">
            {connectors.map(c => {
              const selected = config.connectorId === c.connectorId;
              return (
                <div
                  key={c.connectorId}
                  className={`ec-node-card${selected ? ' active' : ''}`}
                  onClick={() => onChange({ ...config, connectorId: c.connectorId, connectorName: c.name })}
                >
                  {/* 라디오 */}
                  <input
                    type="radio"
                    name="email-connector"
                    className="ec-node-card-radio"
                    checked={selected}
                    onChange={() => onChange({ ...config, connectorId: c.connectorId, connectorName: c.name })}
                    onClick={e => e.stopPropagation()}
                  />

                  {/* 이름 · 계정 */}
                  <div className="ec-node-card-text">
                    <strong>{c.name}</strong>
                    <span>{c.smtpAccount}</span>
                  </div>

                  {/* 편집 버튼 */}
                  <button
                    type="button"
                    className="ec-node-card-edit"
                    onClick={e => { e.stopPropagation(); openEdit(c.connectorId!); }}
                    title="커넥터 설정 편집"
                  >
                    <EditIcon />
                  </button>

                  {/* 삭제 버튼 */}
                  <button
                    type="button"
                    className="ec-node-card-edit ec-node-card-delete"
                    onClick={e => { e.stopPropagation(); handleDelete(c.connectorId!); }}
                    title="커넥터 삭제"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {config.connectorId && (
        <>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">작업 유형</div>
            <Select value={config.operation || 'send'} onChange={e => onChange({ ...config, operation: e.target.value as EmailConnectorNodeConfig['operation'] })}>
              <option value="send">메일 발송</option>
              <option value="read">메일 읽기</option>
              <option value="reply">메일 답장</option>
            </Select>
          </div>
          {(config.operation === 'send' || !config.operation) && <>
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">수신자 <span style={{ color: '#e5484d' }}>*</span></div>
              <div className={`input${showErrors && !config.to?.trim() ? ' input--error' : ''}`}>
                <input type="text" className="input-control" placeholder="a@example.com; b@example.com (세미콜론으로 다중 입력)" value={config.to||''} onChange={e => onChange({ ...config, to: e.target.value })} />
              </div>
            </div>
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">제목</div>
              <div className="input">
                <input type="text" className="input-control" placeholder="메일 제목" value={config.subject||''} onChange={e => onChange({ ...config, subject: e.target.value })} />
              </div>
            </div>
          </>}
          {(config.operation === 'read' || config.operation === 'reply') && <>
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">검색 키워드</div>
              <div className='form-item'>
                <div className="input">
                  <input type="text" className="input-control" placeholder="검색할 키워드" value={config.query||''} onChange={e => onChange({ ...config, query: e.target.value })} />
                </div>
                <small className="form-sublabel">비워두면 최신 메일을 조회합니다.</small>
              </div>
            </div>
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">최대 조회 수</div>
              <Select value={config.maxResults||10} onChange={e => onChange({ ...config, maxResults: parseInt(e.target.value) })}>
                {[5,10,20,50].map(n => <option key={n} value={n}>{n}개</option>)}
              </Select>
            </div>
          </>}
        </>
      )}
      </div>
    </>
  );
}

// ── 네이버 메일 노드 설정 ────────────────────────────────────────────────────

interface NaverMailConfig {
  email?: string;
  appPassword?: string;
  connected?: boolean;
  // 작업 설정
  operation?: 'send' | 'read';
  to?: string;
  subject?: string;
  query?: string;
  maxResults?: number;
  // 설정 단계
  setupStep?: number;
}

interface NaverMailConfigModalProps {
  config: NaverMailConfig;
  onChange: (config: NaverMailConfig) => void;
}

export function NaverMailConfigModal({ config, onChange }: NaverMailConfigModalProps) {
  const step = config.setupStep ?? (config.connected && config.email ? 2 : 1);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');

  const handleTest = async () => {
    if (!config.email || !config.appPassword) return;
    setTesting(true); setTestError('');
    try {
      await connectorApi.testNaverMail(config.email, config.appPassword);
      onChange({ ...config, connected: true, setupStep: 2 });
    } catch (err: unknown) {
      setTestError((err as Error).message || '연결 실패. 이메일/앱 비밀번호를 확인하세요.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="gmail-setup">

      {/* 스텝 인디케이터 */}
      <div className="gmail-steps">
        {[1, 2].map(s => (
          <div key={s} className={`gmail-step-dot${step === s ? ' active' : step > s ? ' done' : ''}`}>
            {step > s ? '✓' : s}
          </div>
        ))}
        <div className="gmail-step-line" />
      </div>

      {/* ── Step 1: 앱 비밀번호 발급 가이드 + 입력 ── */}
      {step === 1 && (
        <div className="gmail-step-content">
          <h4 className="gmail-step-title">1단계 — 네이버 메일 연동 준비</h4>
          <p className="gmail-step-desc">
            네이버 메일을 연동하려면 아래 두 가지를 먼저 설정해야 합니다.
          </p>
          <ol className="gmail-guide-list">
            <li>
              <strong>[IMAP/SMTP 사용 설정]</strong><br />
              <a href="https://mail.naver.com" target="_blank" rel="noreferrer">mail.naver.com</a> 접속 →
              우측 상단 <strong>⚙️ 설정</strong> → <strong>환경설정</strong> →
              <strong>POP3/IMAP 설정</strong> → <strong>IMAP/SMTP 사용함</strong> 선택 → 저장<br />
              <span className="gmail-guide-sub">※ 이 설정이 꺼져 있으면 앱 비밀번호가 맞아도 연결 거부됩니다</span>
            </li>
            <li>
              <strong>[앱 비밀번호 발급]</strong><br />
              <a href="https://nid.naver.com/user2/help/myInfo" target="_blank" rel="noreferrer">
                네이버 내 정보
              </a> 접속 → <strong>보안설정</strong> →
              <strong>2단계 인증 관리</strong> → <strong>애플리케이션 비밀번호 관리</strong><br />
              <span className="gmail-guide-sub">※ 2단계 인증이 활성화되어야 앱 비밀번호 발급 가능</span>
            </li>
            <li>
              종류선택 → <strong>직접 입력</strong> → 이름 입력 (예: <code className="gmail-code">AgentBuilder</code>)
              → <strong>생성하기</strong>
            </li>
            <li>
              <strong>비밀번호 확인</strong>란에 표시된 <strong>12자리 비밀번호</strong> 복사<br />
              <span className="gmail-guide-sub">※ 창을 닫으면 다시 볼 수 없습니다. 바로 복사하세요.</span>
            </li>
          </ol>

          <div className="form-item" style={{ marginTop: '16px' }}>
            <label className='form-label'>네이버 이메일</label>
            <input type="text"
              placeholder="example@naver.com"
              value={config.email || ''}
              onChange={(e) => onChange({ ...config, email: e.target.value })}
            />
          </div>
          <div className="form-item">
            <label className='form-label'>앱 비밀번호 (12자리)</label>
            <input type="password"
              placeholder="발급받은 12자리 앱 비밀번호"
              value={config.appPassword || ''}
              onChange={(e) => onChange({ ...config, appPassword: e.target.value })}
            />
          </div>

          {testError && (
            <Notice color="error">{testError}</Notice>
          )}

          <Button
            color="primary"
            style={{ width: '100%', marginTop: '12px' }}
            disabled={!config.email || !config.appPassword || testing}
            onClick={handleTest}
          >
            {testing ? '연결 확인 중...' : '🔗 연결 테스트 →'}
          </Button>
        </div>
      )}

      {/* ── Step 2: 작업 설정 ── */}
      {step === 2 && (
        <div className="gmail-step-content">
          {config.email && (
            <div className="gmail-connection-card">
              <div className="gmail-connection-info">
                <div className="gmail-connection-icon">✅</div>
                <div>
                  <div className="gmail-connection-email">{config.email}</div>
                  <div className="gmail-connection-sub">네이버 메일 계정이 연결되어 있습니다</div>
                </div>
              </div>
              <div className="gmail-connection-actions">
                <button className="gmail-reauth-btn" onClick={() => {
                  onChange({ ...config, connected: false, appPassword: '', setupStep: 1 });
                }}>🔄 재연결</button>
              </div>
            </div>
          )}
          <div className="node-divider" />
          <h4 className="gmail-step-title node-section-title">작업 설정</h4>
          <div className="form-item">
            <label className='form-label'>작업 유형</label>
            <Select
              value={config.operation || 'send'}
              onChange={(e) => onChange({ ...config, operation: e.target.value as NaverMailConfig['operation'] })}
            >
              <option value="send">메일 발송</option>
              <option value="read">메일 읽기</option>
            </Select>
          </div>

          {(config.operation === 'send' || !config.operation) && (
            <>
              <div className="form-item">
                <label className='form-label'>수신자</label>
                <input type="text"
                  placeholder="recipient@example.com"
                  value={config.to || ''}
                  onChange={(e) => onChange({ ...config, to: e.target.value })}
                />
              </div>
              <div className="form-item">
                <label className='form-label'>제목</label>
                <input type="text"
                  placeholder="메일 제목 (변수 사용 가능: {{변수명}})"
                  value={config.subject || ''}
                  onChange={(e) => onChange({ ...config, subject: e.target.value })}
                />
              </div>
            </>
          )}

          {config.operation === 'read' && (
            <>
              <div className="form-item">
                <label className='form-label'>검색 키워드<span className='optional'>(선택)</span></label>
                <input type="text"
                  placeholder="검색어 입력 (비워두면 최신 메일 조회)"
                  value={config.query || ''}
                  onChange={(e) => onChange({ ...config, query: e.target.value })}
                />
              </div>
              <div className="form-item">
                <label className='form-label'>최대 조회 수</label>
                <Select
                  value={config.maxResults || 10}
                  onChange={(e) => onChange({ ...config, maxResults: parseInt(e.target.value) })}
                >
                  <option value={5}>5개</option>
                  <option value={10}>10개 (기본)</option>
                  <option value={20}>20개</option>
                  <option value={50}>50개</option>
                </Select>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ── Notion 노드 설정 ─────────────────────────────────────────────────────────
interface NotionConfig {
  // 연결 설정
  integrationToken?: string;
  connected?: boolean;
  workspaceName?: string;
  // 작업 설정
  operation?: 'create_page' | 'add_db_item' | 'watch_db' | 'watch_page';
  pageId?: string;
  databaseId?: string;
  filterProperty?: string;
  filterValue?: string;
  filterCondition?: 'equals' | 'contains' | 'is_empty' | 'is_not_empty';
  newPageTitle?: string;
  newPageContent?: string;
  // 설정 단계
  setupStep?: number;
}

interface NotionConfigModalProps {
  config: NotionConfig;
  onChange: (config: NotionConfig) => void;
  showErrors?: boolean;
}

const NOTION_STEPS = ['Integration 생성', '토큰 입력', '작업 설정'];

const getNotionStep = (config: NotionConfig) =>
  config.setupStep ?? (config.connected && config.integrationToken ? 3 : 1);

/** 스텝 인디케이터 — 모달 subHeader 로 사용 */
export function NotionSteps({ config }: { config: NotionConfig }) {
  return <NodeSetupSteps labels={NOTION_STEPS} step={getNotionStep(config)} />;
}

export function NotionConfigModal({ config, onChange, showErrors }: NotionConfigModalProps) {
  const step = getNotionStep(config);
  const setStep = (s: number) => onChange({ ...config, setupStep: s });
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');

  const handleTestConnection = async () => {
    if (!config.integrationToken) return;
    setTesting(true);
    setTestError('');
    try {
      const data = await connectorApi.testNotionToken(config.integrationToken);
      onChange({ ...config, connected: true, workspaceName: data.workspaceName, setupStep: 3, operation: config.operation || 'watch_db' });
    } catch (err: unknown) {
      setTestError((err as Error).message || '연결 실패. 토큰을 확인하세요.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="gmail-setup">

      {/* ── Step 1: Integration 생성 가이드 ── */}
      {step === 1 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Notion Integration 생성</div>
            <p className="node-guide-desc">Notion API를 사용하려면 Integration을 먼저 생성해야 합니다. 프로젝트당 한 번만 하면 됩니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. Integration 만들기</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-1</span>
              <div>
                <strong>콘솔 로그인</strong>
                <p>
                  <a href="https://www.notion.so/my-integrations" target="_blank" rel="noreferrer">www.notion.so/my-integrations</a> 접속 후 Notion 계정 로그인
                </p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-2</span>
              <div>
                <strong>새 API 통합 만들기</strong>
                <p>오른쪽 상단 + 새 API 통합</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-3</span>
              <div>
                <strong>기본 정보 입력</strong>
                <p>이름 입력 (예: <code className="gmail-code">AgentBuilder</code>) → 연결할 워크스페이스 선택 → 유형: 내부</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. 권한 설정 및 토큰 복사</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div>
                <strong>기능 권한 체크</strong>
                <p>기능 섹션에서 아래 3개 체크 → 하단 저장</p>
                <ul className="node-scope-list">
                  <li><code className="gmail-code">콘텐츠 읽기</code></li>
                  <li><code className="gmail-code">콘텐츠 업데이트</code></li>
                  <li><code className="gmail-code">콘텐츠 삽입</code></li>
                </ul>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>시크릿 복사</strong>
                <p>상단 내부 통합 시크릿 옆 복사 · <code className="gmail-code">secret_xxxxxxxx</code> 형태의 토큰입니다</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 1 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" onClick={() => setStep(2)}>다음</Button>
        </div>
      )}

      {/* ── Step 2: 토큰 입력 + 연결 테스트 ── */}
      {step === 2 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Integration 토큰 입력</div>
            <p className="node-guide-desc">1단계에서 복사한 토큰을 붙여넣고 연결을 테스트하세요.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">Integration Token <span className="required">*</span></div>
            <div className="input">
              <input
                type="password"
                className="input-control"
                placeholder="secret_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={config.integrationToken || ''}
                onChange={(e) => onChange({ ...config, integrationToken: e.target.value, connected: false })}
              />
            </div>
            <small className="form-sublabel">토큰은 암호화되어 서버에 저장됩니다.</small>
          </div>

          {testError && <Notice color="error">{testError}</Notice>}
        </div>
      )}
      {step === 2 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(1)}>이전</Button>
          <Button
            color="primary"
            disabled={!config.integrationToken || testing}
            onClick={handleTestConnection}
          >
            {testing ? '연결 확인 중...' : '연결 테스트'}
          </Button>
        </div>
      )}

      {/* ── Step 3: 페이지/DB 공유 가이드 + 작업 설정 ── */}
      {step === 3 && (
        <div className="bedrock-guide">
          {/* 연결된 워크스페이스 */}
          {config.connected && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">연결된 워크스페이스</div>
              <div className="node-account-card bedrock-guide-step">
                <div className="node-account-info">
                  <span className="node-account-icon" />
                  <div className="node-account-text">
                    <strong>{config.workspaceName || 'Notion 워크스페이스'}</strong>
                    <span>Integration이 연결되어 있습니다</span>
                  </div>
                </div>
                <div className="node-account-actions">
                  <Button color="gray" appearance="outline" size="sm" onClick={() => onChange({ ...config, connected: false, integrationToken: '', setupStep: 2 })}>
                    <RefreshIcon /> 토큰 재입력
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 페이지/DB 공유 안내 */}
          <Notice color="warning" title="접근할 페이지/DB를 Integration에 공유해야 합니다">
            <ul>
              <li>Notion에서 접근할 <strong>페이지 또는 데이터베이스</strong>를 엽니다</li>
              <li>오른쪽 상단 <strong>···</strong> (더보기) 버튼 클릭</li>
              <li><strong>연결 추가</strong> 클릭 → 생성한 Integration 검색 → 선택</li>
              <li>하위 페이지는 자동으로 공유됩니다</li>
            </ul>
          </Notice>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">작업 유형</div>
            <Select
              value={config.operation || 'watch_db'}
              onChange={(e) => onChange({ ...config, operation: e.target.value as NotionConfig['operation'] })}
            >
              <optgroup label="감지">
                <option value="watch_db">새 DB 항목 감지</option>
                <option value="watch_page">새 하위 페이지 감지</option>
              </optgroup>
              <optgroup label="쓰기">
                <option value="create_page">새 페이지 생성</option>
                <option value="add_db_item">새 데이터베이스 생성</option>
              </optgroup>
            </Select>
          </div>

          {/* DB ID 입력 */}
          {(config.operation === 'watch_db' || config.operation === 'add_db_item') && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">데이터베이스 ID</div>
              <div className='form-item'>
                <div className="input">
                  <input
                    type="text"
                    className="input-control"
                    placeholder="https://app.notion.com/p/xxxxxxxx..."
                    value={config.databaseId || ''}
                    onChange={(e) => onChange({ ...config, databaseId: e.target.value })}
                  />
                </div>
                <small className="form-sublabel">DB 페이지 URL 또는 ID 입력</small>
              </div>
            </div>
          )}

          {/* 페이지 ID 입력 (페이지 감지 / 생성) */}
          {(config.operation === 'watch_page' || config.operation === 'create_page') && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">페이지 ID</div>
              <div className='form-item'>
                <div className="input">
                  <input
                    type="text"
                    className="input-control"
                    placeholder="https://app.notion.com/p/xxxxxxxx..."
                    value={config.pageId || ''}
                    onChange={(e) => onChange({ ...config, pageId: e.target.value })}
                  />
                </div>
                <small className="form-sublabel">감지하거나 하위 페이지를 생성할 부모 페이지 URL 입력</small>
              </div>
            </div>
          )}

          {/* 감지 안내 */}
          {(config.operation === 'watch_db' || config.operation === 'watch_page') && (
            <Notice color="info" title="동작 방식">
              스케줄 트리거 노드와 연결하면 설정한 주기마다 확인하여, 마지막 확인 이후{' '}
              <strong>새로 추가된 {config.operation === 'watch_db' ? 'DB 항목' : '하위 페이지'}</strong>이 있을 때 다음 노드로 전달합니다.
            </Notice>
          )}

          {/* 새 페이지 생성 필드 */}
          {config.operation === 'create_page' && (
            <>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">페이지 제목</div>
                <div className="input">
                  <input
                    type="text"
                    className="input-control"
                    placeholder="새 페이지 제목 (변수 사용 가능: {{변수명}})"
                    value={config.newPageTitle || ''}
                    onChange={(e) => onChange({ ...config, newPageTitle: e.target.value })}
                  />
                </div>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title form-label mb-0">내용<span className='optional'>(선택)</span></div>
                <textarea
                  className="config-textarea"
                  rows={3}
                  placeholder="페이지 본문 내용 (비워두면 빈 페이지 생성)"
                  value={config.newPageContent || ''}
                  onChange={(e) => onChange({ ...config, newPageContent: e.target.value })}
                />
              </div>
            </>
          )}

          {/* DB 항목 추가 필드 */}
          {config.operation === 'add_db_item' && (
            <>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">항목 제목</div>
                <div className="input">
                  <input
                    type="text"
                    className="input-control"
                    placeholder="새 항목 제목 (변수 사용 가능: {{변수명}})"
                    value={config.newPageTitle || ''}
                    onChange={(e) => onChange({ ...config, newPageTitle: e.target.value })}
                  />
                </div>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title form-label mb-0">내용 <span className='optional'>(선택)</span></div>
                <textarea
                  className="config-textarea"
                  rows={2}
                  placeholder="항목 내용 또는 AI 응답 변수: {{response}}"
                  value={config.newPageContent || ''}
                  onChange={(e) => onChange({ ...config, newPageContent: e.target.value })}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// 타입 export
// ── Google Drive 노드 설정 ────────────────────────────────────────────────────

const GDRIVE_STEPS = ['프로젝트 설정', 'OAuth 클라이언트', '계정 연결', '작업 설정'];

// 리디렉션 URI: 운영환경은 같은 origin, 개발환경은 백엔드 포트(3002)로 변경
const getGdriveRedirectUri = () => {
  const origin = window.location.origin;
  // 개발환경 (Vite dev server 3003 → 백엔드 3002)
  if (origin.includes('localhost:3003') || origin.includes('127.0.0.1:3003')) {
    return origin.replace(':3003', ':3002') + '/api/auth/google-drive/callback';
  }
  // 개발환경 (Vite dev server 5173 → 백엔드 3002)
  if (origin.includes('localhost:5173') || origin.includes('127.0.0.1:5173')) {
    return origin.replace(':5173', ':3002') + '/api/auth/google-drive/callback';
  }
  // 운영환경: 같은 origin 사용
  return origin + '/api/auth/google-drive/callback';
};
// 안내용 예시 URI (운영환경 기준)
const GDRIVE_REDIRECT_URI_EXAMPLE = 'https://acxion-on.etechcloud.co.kr/api/auth/google-drive/callback';

export const getGoogleDriveStep =(config: GoogleDriveConfig) =>
  config.setupStep ?? (config.connected && config.clientId ? 4 : 1);

/** 단계형 노드 설정 공통 스텝 인디케이터 — 모달 subHeader 로 사용 */
/** 단계 이동 버튼 → 모달 푸터(#node-config-footer-actions)로 포털 */
function StepFooter({ children }: { children: React.ReactNode }) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setEl(document.getElementById('node-config-footer-actions'));
  }, []);
  return el ? createPortal(children, el) : null;
}
const navPortal = (node: React.ReactNode) => <StepFooter>{node}</StepFooter>;

/**
 * 단계형 노드의 푸터 정보 (WorkflowBuilderPage 에서 사용)
 * - midSave: 중간 저장 버튼을 보여줄 단계 (연결 확인 · 연결 테스트처럼 진행 상태를 남길 의미가 있는 단계)
 * - 마지막 단계는 공통 저장 버튼
 */
export function getNodeSetupFooter(type: string, config: unknown): { step: number; last: number; midSave: boolean } | null {
  const info = (step: number, last: number, saveSteps: number[]) => ({ step, last, midSave: saveSteps.includes(step) });
  switch (type) {
    case 'google-drive': return info(getGoogleDriveStep(config as GoogleDriveConfig), 4, [3]);
    case 'gmail':        return info(getGmailStep(config as GmailConfig), 4, [3]);
    case 'slack':        return info(getSlackStep(config as SlackConfig), 3, [2]);
    case 'notion':       return info(getNotionStep(config as NotionConfig), 3, [2]);
    case 'mcp':          return info(getMcpStep(config as McpConfig), 2, [1]);
    case 'dropbox':      return info(getDropboxStep(config as DropboxConfig), 4, [3]);
    default:             return null;
  }
}

function NodeSetupSteps({ labels, step }: { labels: string[]; step: number }) {
  return (
    <div className="bedrock-steps node-config-steps">
      {labels.map((label, i) => {
        const s = i + 1;
        return (
          <Fragment key={label}>
            {i > 0 && <span className={`node-config-step-line ${step >= s ? 'done' : ''}`} aria-hidden="true" />}
            <div className={`bedrock-step ${step === s ? 'active' : ''} ${step > s ? 'done' : ''}`}>
              <span className="bedrock-step-num">{step > s ? '✓' : s}</span>
              <span className="bedrock-step-label">{label}</span>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

export function GoogleDriveSteps({ config }: { config: GoogleDriveConfig }) {
  return <NodeSetupSteps labels={GDRIVE_STEPS} step={getGoogleDriveStep(config)} />;
}

export function GoogleDriveConfigModal({ config, onChange }: { config: GoogleDriveConfig; onChange: (config: GoogleDriveConfig) => void }) {
  const step = getGoogleDriveStep(config);
  const setStep = (s: number) => onChange({ ...config, setupStep: s });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const { showToast } = useToast();

  // 마운트 시 기존 설정 로드 — 노드에 clientId가 저장된 경우만 동기화
  useEffect(() => {
    connectorApi.getGoogleDriveConfig().then(saved => {
      if (saved) {
        onChange({
          ...config,
          clientId: saved.clientId,
          connected: saved.connected,
          connectedEmail: saved.connectedEmail,
          setupStep: saved.connected ? 4 : (saved.clientId ? 3 : config.setupStep ?? 1),
        });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className="gmail-setup">

      {/* ── Step 1: Google Cloud 설정 가이드 ── */}
      {step === 1 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Google Cloud 프로젝트 설정</div>
            <p className="node-guide-desc">Google Drive API를 사용하려면 Google Cloud에서 앱을 등록해야 합니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. 프로젝트 만들기</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-1</span>
              <div>
                <strong>콘솔 로그인</strong>
                <p>
                  <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a> 접속 후 Google 계정 로그인
                </p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-2</span>
              <div>
                <strong>새 프로젝트 선택</strong>
                <p>상단 프로젝트 이름 → 새 프로젝트 · Gmail 프로젝트가 있으면 그대로 사용 가능</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. Google Drive API 사용 설정</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div>
                <strong>라이브러리 열기</strong>
                <p>왼쪽 메뉴 → API 및 서비스 → 라이브러리</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>API 검색 후 사용 설정</strong>
                <p>검색창에 <code className="gmail-code">Google Drive API</code> 입력 → 사용 설정</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">3. OAuth 동의 화면 구성</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-1</span>
              <div>
                <strong>OAuth 동의 화면 열기</strong>
                <p>왼쪽 메뉴 → OAuth 동의 화면</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-2</span>
              <div>
                <strong>권한 범위 추가</strong>
                <p>데이터 액세스 → 범위 추가 → <code className="gmail-code">drive</code> 검색 → 아래 2개 체크</p>
                <ul className="node-scope-list">
                  <li><code className="gmail-code">drive.readonly</code> 파일·폴더 감지 및 읽기</li>
                  <li><code className="gmail-code">drive.file</code> 파일 업로드</li>
                </ul>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-3</span>
              <div>
                <strong>테스트 사용자 추가</strong>
                <p>인증 센터 → 테스트 사용자 → 사용자 추가 → 본인 Google 주소 입력</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 1 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" onClick={() => setStep(2)}>다음</Button>
        </div>
      )}

      {/* ── Step 2: Client ID / Secret 입력 ── */}
      {step === 2 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">OAuth 클라이언트 ID 생성</div>
            <p className="node-guide-desc">Google 계정 연결에 사용할 OAuth 클라이언트를 만들고 발급된 값을 입력합니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. OAuth 클라이언트 만들기</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-1</span>
              <div>
                <strong>클라이언트 만들기 시작</strong>
                <p>왼쪽 메뉴 → 클라이언트 → 오른쪽 상단 OAuth 클라이언트 만들기</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-2</span>
              <div>
                <strong>애플리케이션 정보 입력</strong>
                <p>유형: 웹 애플리케이션 · 이름: 자유 입력 (예: <code className="gmail-code">AgentBuilder</code>)</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-3</span>
              <div className="node-guide-step-body">
                <strong>승인된 리디렉션 URI 추가</strong>
                <p>승인된 리디렉션 URI → URI 추가 → 아래 값을 복사하여 붙여넣기</p>
                <div className="bedrock-ext-row node-copy-row">
                  <input className="input-control" type="text" value={GDRIVE_REDIRECT_URI_EXAMPLE} readOnly />
                  <button
                    type="button"
                    className="bedrock-copy-btn node-copy-btn"
                    title="복사"
                    onClick={() => { navigator.clipboard.writeText(GDRIVE_REDIRECT_URI_EXAMPLE); showToast('success', '리디렉션 URI 복사됨'); }}
                  ><CopyIcon /></button>
                </div>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-4</span>
              <div>
                <strong>만들기 후 값 복사</strong>
                <p>만들기 → 팝업에 표시되는 클라이언트 ID · 클라이언트 보안 비밀번호 복사</p>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. 클라이언트 ID 입력 <span className="required">*</span></div>
            <div className="input">
              <input type="text" className="input-control"
                placeholder="123456789-xxx.apps.googleusercontent.com"
                value={config.clientId || ''}
                onChange={(e) => onChange({ ...config, clientId: e.target.value })}
              />
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">3. 클라이언트 보안 비밀번호 입력 <span className="required">*</span></div>
            <div className="input">
              <input type="password" className="input-control"
                placeholder="GOCSPX-xxxxxxxxxx"
                value={config.clientSecret || ''}
                onChange={(e) => onChange({ ...config, clientSecret: e.target.value })}
              />
            </div>
            {saveError && <div className="node-save-error">{saveError}</div>}
          </div>
        </div>
      )}
      {step === 2 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(1)}>이전</Button>
          <Button
            color="primary"
            disabled={!config.clientId || !config.clientSecret || saving}
            onClick={async () => {
              setSaving(true); setSaveError('');
              try {
                await connectorApi.saveGoogleDriveConfig(config.clientId!, config.clientSecret!);
                setStep(3);
              } catch {
                setSaveError('저장 실패. 다시 시도해주세요.');
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? '저장 중...' : '저장 후 다음'}
          </Button>
        </div>
      )}

      {/* ── Step 3: Google 계정 연결 ── */}
      {step === 3 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Google 계정 연결</div>
            <p className="node-guide-desc">Google Drive에 접근할 계정으로 로그인하여 아래 권한을 허용합니다.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. 허용되는 권한</div>
            <div className="node-permission-list">
              <div className="node-permission-item"><PlusIcon />새 파일 추가 감지</div>
              <div className="node-permission-item"><EditIcon />파일 수정 감지</div>
              <div className="node-permission-item"><UploadIcon />파일 업로드</div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. 계정 연결</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div className="node-guide-step-body">
                <strong>Google 로그인</strong>
                <p>아래 버튼을 클릭하면 Google 로그인 창이 열립니다. Google Drive에 접근할 계정으로 로그인해주세요.</p>
                <button
                  type="button"
                  className="btn-google node-oauth-btn"
                  onClick={async () => {
                    try {
                      const url = await connectorApi.getGoogleDriveAuthUrl();
                      const popup = window.open(url, 'google-drive-auth', 'width=500,height=600');
                      const handler = (e: MessageEvent) => {
                        if (e.data?.type === 'GOOGLE_DRIVE_CONNECTED') {
                          window.removeEventListener('message', handler);
                          popup?.close();
                          onChange({ ...config, connected: true, connectedEmail: e.data.email, setupStep: 4 });
                        }
                      };
                      window.addEventListener('message', handler);
                    } catch {
                      alert('인증 URL을 가져오는데 실패했습니다. Client ID/Secret을 확인하세요.');
                    }
                  }}
                >
                  <img src={googleDriveLogo} alt="" /> Google 계정으로 연결
                </button>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>연결 확인</strong>
                <p>로그인 완료 후 아래 연결 확인 버튼을 눌러주세요</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 3 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(2)}>이전</Button>
          <Button
            color="primary"
            onClick={async () => {
                try {
                  const saved = await connectorApi.getGoogleDriveConfig();
                  if (saved?.connected) {
                    onChange({ ...config, connected: true, connectedEmail: saved.connectedEmail, setupStep: 4 });
                  } else {
                    alert('아직 연결되지 않았습니다. Google 계정 연결 버튼을 먼저 클릭해주세요.');
                  }
                } catch {
                  alert('연결 상태 확인 실패. 다시 시도해주세요.');
                }
              }}
            >
            연결 확인
          </Button>
        </div>
      )}

      {/* ── Step 4: 작업 설정 ── */}
      {step === 4 && (
        <div className="bedrock-guide">
          {/* 연결된 계정 */}
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">연결된 계정</div>
            {config.connectedEmail && (
            <div className="node-account-card bedrock-guide-step">
              <div className="node-account-info">
                <span className="node-account-icon">
                </span>
                <div className="node-account-text">
                  <strong>{config.connectedEmail}</strong>
                  <span> Google Drive 계정이 연결되어 있습니다</span>
                </div>
              </div>
              <div className="node-account-actions">
                <Button color="gray" appearance="outline" size="sm" onClick={async () => {
                  await connectorApi.disconnectGoogleDrive().catch(() => {});
                  onChange({ ...config, connected: false, connectedEmail: undefined, setupStep: 3 });
                }}>
                  <RefreshIcon /> 계정 재연결
                </Button>
                <Button color="gray" appearance="outline" size="sm" onClick={() => setStep(2)}>
                  <SettingIcon /> Client ID 재설정
                </Button>
              </div>
            </div>
            )}
          </div>

          {/* 작업 설정 */}
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">작업 설정</div>

            {/* 지원 파일 형식 공통 안내 */}
            <div className="bedrock-guide-step">
              <div className='gdrive-legend'>
                <span>모든 파일 형식 (새 파일 추가, 수정 감지) :</span>
                <div className='gdrive-legend-tag watch'>
                  <span className='gdrive-legend-check'><CheckIcon /></span>
                  감지 가능
                </div>
                <span className="gdrive-legend-sep">/</span> 
                <span>모든 파일 형식 :</span>
                <div className='gdrive-legend-tag upload'>
                  <span className='gdrive-legend-check'><CheckIcon /></span>
                  업로드 가능
                </div>
              </div>
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">작업 유형</div>
            <Select
              value={config.operation || 'watch_new'}
              onChange={(e) => onChange({ ...config, operation: e.target.value as GoogleDriveConfig['operation'] })}
            >
              <optgroup label="감지">
                <option value="watch_new">새 파일 감지</option>
                <option value="watch_modified">파일 수정 감지</option>
              </optgroup>
              <optgroup label="쓰기">
                <option value="upload">파일 업로드</option>
              </optgroup>
            </Select>
          </div>

          {(config.operation === 'watch_new' || config.operation === 'watch_modified' || !config.operation) && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">폴더 ID {config.operation?.startsWith('watch') ? '' : '(선택)'}</div>
              <div className="input">
                <input type="text" className="input-control"
                  placeholder="폴더 URL 또는 ID"
                  value={config.folderId || ''}
                  onChange={(e) => onChange({ ...config, folderId: e.target.value })}
                />
              </div>
              {(config.operation === 'watch_new' || config.operation === 'watch_modified') && (
                <small className="form-sublabel">
                  스케줄 트리거와 연결하면 주기마다 폴더를 확인하여, 마지막 확인 이후{' '}
                  <strong>{config.operation === 'watch_new' ? '새로 추가된 파일' : '수정된 파일'}</strong>이 있을 때 다음 노드로 전달합니다.
                </small>
              )}
              {/* list 필터는 현재 미사용 */}
            </div>
          )}

          {config.operation === 'read' && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">파일 ID</div>
              <div className="input">
                <input type="text" className="input-control"
                  placeholder="파일 URL 또는 ID"
                  value={config.fileId || ''}
                  onChange={(e) => onChange({ ...config, fileId: e.target.value })}
                />
              </div>
            </div>
          )}

          {config.operation === 'upload' && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">업로드할 파일</div>
              {/* 드롭존 */}
              <div
                className="pipe-file-dropzone drive-upload-dropzone"
                onClick={() => {
                  const input = document.getElementById('drive-upload-input') as HTMLInputElement;
                  input?.click();
                }}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('dragover');
                  const file = e.dataTransfer.files[0];
                  if (file) onChange({ ...config, uploadFileName: file.name, uploadFile: file });
                }}
              >
                <div className="pipe-file-dropzone-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                    <polyline points="13 2 13 9 20 9" />
                  </svg>
                </div>
                <div className="pipe-file-dropzone-text">파일을 드래그하거나 클릭해서 선택</div>
                <div className="pipe-file-dropzone-sub">모든 파일 형식 지원 (최대 50MB)</div>
                <input
                  id="drive-upload-input"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onChange({ ...config, uploadFileName: file.name, uploadFile: file });
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
              </div>

              {/* 선택된 파일 */}
              {config.uploadFileName && (
                <div className="file-item pipe-file-item drive-upload-file-item">
                  <span className="pipe-file-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                      <polyline points="13 2 13 9 20 9" />
                    </svg>
                  </span>
                  <div className="pipe-file-info">
                    <div className="pipe-file-name">{config.uploadFileName}</div>
                    {config.uploadFile && (
                      <div className="pipe-file-meta">
                        {config.uploadFile.size < 1024 * 1024
                          ? `${(config.uploadFile.size / 1024).toFixed(1)} KB`
                          : `${(config.uploadFile.size / (1024 * 1024)).toFixed(1)} MB`}
                      </div>
                    )}
                  </div>
                  <Badge color="success" dot>준비됨</Badge>
                  <button className="pipe-file-remove" onClick={() => onChange({ ...config, uploadFileName: undefined, uploadFile: undefined })}>✕</button>
                </div>
              )}

            </div>
          )}

          {config.operation === 'upload' && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">저장할 폴더 ID (선택)</div>
              <div className='form-item'>
                <div className="input">
                  <input type="text" className="input-control"
                    placeholder="폴더 URL 또는 ID"
                    value={config.folderId || ''}
                    onChange={(e) => onChange({ ...config, folderId: e.target.value })}
                  />
                </div>
                <small className="form-sublabel">비우면 내 드라이브 루트에 저장됩니다.</small>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── 웹 검색 노드 설정 ────────────────────────────────────────────────────────

type WebSearchProvider = 'tavily' | 'serper' | 'brave' | 'you';

interface WebSearchConfig {
  provider?: WebSearchProvider;
  apiKey?: string;
  connected?: boolean;
}

interface WebSearchConfigModalProps {
  config: WebSearchConfig;
  onChange: (config: WebSearchConfig) => void;
}

// 지원 가능 엔진 메타데이터 (API 키 발급처 안내 포함)
const WEB_SEARCH_PROVIDERS: Array<{
  value: WebSearchProvider;
  label: string;
  desc: string;
  keyUrl: string;
}> = [
  { value: 'tavily', label: 'Tavily', desc: 'LLM·RAG 특화 검색. 답변 요약 제공. (무료 플랜 월 1,000회부터)', keyUrl: 'https://app.tavily.com' },
  { value: 'serper', label: 'Serper', desc: 'Google 검색 결과. 가입 시 무료 2,500회 제공.', keyUrl: 'https://serper.dev' },
  { value: 'brave', label: 'Brave', desc: '독립 검색 인덱스. 프라이버시 중심. (무료 플랜 월 2,000회부터)', keyUrl: 'https://api-dashboard.search.brave.com' },
  { value: 'you', label: 'You.com', desc: 'LLM·에이전트 특화 검색. 신규 가입 시 $100 크레딧 제공.', keyUrl: 'https://you.com/platform' },
];

export function WebSearchConfigModal({ config, onChange }: WebSearchConfigModalProps) {
  const provider = config.provider || 'tavily';
  const [apiKeyInput, setApiKeyInput] = useState(config.apiKey && config.apiKey !== '••••••••' ? config.apiKey : '');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');

  // 마운트 시 기존 커넥터 설정 자동 로드
  useEffect(() => {
    connectorApi.getWebSearchConfig().then(saved => {
      if (saved?.connected) {
        onChange({ ...config, provider: (saved.provider as typeof provider) || 'tavily', apiKey: saved.apiKey, connected: true });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedMeta = WEB_SEARCH_PROVIDERS.find(p => p.value === provider)!;

  const handleTestConnection = async () => {
    if (!apiKeyInput.trim()) {
      setTestError('API 키를 입력하세요.');
      return;
    }
    setTesting(true);
    setTestError('');
    try {
      await connectorApi.testWebSearch(provider, apiKeyInput.trim());
      onChange({ ...config, provider, apiKey: apiKeyInput.trim(), connected: true });
    } catch (err: unknown) {
      setTestError((err as Error).message || '연결 실패. API 키를 확인하세요.');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    setTesting(true);
    try {
      await connectorApi.disconnectWebSearch();
    } catch {
      /* 조용히 무시 — 로컬 상태는 어차피 초기화 */
    } finally {
      setApiKeyInput('');
      onChange({ ...config, connected: false, apiKey: undefined });
      setTesting(false);
    }
  };

  return (
    <div className='form-grid'>
      {/* 지원 가능 엔진 선택 */}
      <div className="form-item">
        {/* 안내 문구 */}
        <p className="form-guide mb-20">
          * 웹 검색은 아래 <strong>지원 가능 엔진</strong> 중 하나를 선택하고 <strong>본인 계정의 API 키</strong>를 등록하면 됩니다.
          무료·유료 플랜은 각 제공자 계정에서 발급받은 키에 따라 적용됩니다.
          연결 후에는 챗봇이 질문 내용을 보고 <strong>필요할 때 자동으로 웹을 검색</strong>해 답변합니다.
          (검색어를 미리 지정하지 않아도 됩니다.)
        </p>
        <label className='form-label'>지원 가능 엔진<span className='required'>*</span></label>
        <Select
          value={provider}
          disabled={config.connected}
          onChange={e => onChange({ ...config, provider: e.target.value as WebSearchProvider })}
        >
          {WEB_SEARCH_PROVIDERS.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </Select>
        <small className="form-sublabel">{selectedMeta.desc}</small>
      </div>

      {config.connected ? (
        /* ── 연결됨 상태 ── */
        <div className="form-item form-item--full">
          <div className="node-account-card bedrock-guide-step">
            <div className="node-account-info">
              <span className="node-account-icon" />
              <div className="node-account-text">
                <strong>{selectedMeta.label}</strong>
                <span>웹 검색 엔진이 연결되어 있습니다</span>
              </div>
            </div>
            <div className="node-account-actions">
              <Button
                color='gray'
                appearance='outline'
                size='sm'
                onClick={handleDisconnect}
                disabled={testing}
              >
                연결 해제
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* ── 미연결 상태: API 키 입력 ── */
        <>
          <div className="form-item">
            <label className='form-label'>API 키<span className='required'>*</span></label>
            <input
              type="password"
              placeholder={`${selectedMeta.label} API 키를 입력하세요`}
              value={apiKeyInput}
              onChange={e => setApiKeyInput(e.target.value)}
            />
            <small className="form-sublabel">
              API 키 발급:{' '}
              <a href={selectedMeta.keyUrl} target="_blank" rel="noreferrer">
                {selectedMeta.keyUrl}
              </a>
            </small>
          </div>

          {testError && (
            <div className="form-item form-item--full">
              <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }}>{testError}</p>
            </div>
          )}

          <div className="form-item form-item--full">
            <button
              type="button"
              className="btn-primary"
              style={{ width: '100%' }}
              onClick={handleTestConnection}
              disabled={testing || !apiKeyInput.trim()}
            >
              {testing ? '연결 테스트 중...' : '연결 테스트 및 저장'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── MCP 서버 노드 설정 ────────────────────────────────────────────────────────

interface McpConfig {
  serverUrl?: string;
  serverName?: string;
  connected?: boolean;
  setupStep?: number;
  authType?: 'oauth' | 'apikey';
  apiKey?: string;
}

interface McpConfigModalProps {
  config: McpConfig;
  onChange: (config: McpConfig) => void;
}

const MCP_STEPS = ['서버 연결', '연결 완료'];

const getMcpStep = (config: McpConfig) =>
  config.setupStep ?? (config.connected && config.serverUrl ? 2 : 1);

/** 스텝 인디케이터 — 모달 subHeader 로 사용 */
export function McpSteps({ config }: { config: McpConfig }) {
  return <NodeSetupSteps labels={MCP_STEPS} step={getMcpStep(config)} />;
}

export function McpConfigModal({ config, onChange }: McpConfigModalProps) {
  const step = getMcpStep(config);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const authType = config.authType ?? 'oauth';

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'MCP_CONNECTED') {
        onChange({ ...config, connected: true, setupStep: 2 });
        setConnecting(false);
        setError('');
      }
      // 인증 실패 → 연결 상태 해제 및 에러 표시 (재시도 팝업은 열지 않음)
      if (e.data?.type === 'MCP_AUTH_FAILED') {
        setConnecting(false);
        setError(e.data.message || 'MCP 인증에 실패했습니다.');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    if (!config.serverUrl?.trim()) return;
    setConnecting(true);
    setError('');
    try {
      // API Key 방식: apiKey 전달해서 바로 연결 확인
      // OAuth 방식: 팝업으로 처리 (listMcpTools 내부에서 OAuth 플로우)
      await connectorApi.listMcpTools(
        config.serverUrl,
        authType === 'apikey' ? (config.apiKey || undefined) : undefined
      );
      onChange({ ...config, connected: true, setupStep: 2 });
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'MCP 서버 연결에 실패했습니다.');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="gmail-setup">

      {/* ── Step 1: 서버 연결 ── */}
      {step === 1 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">MCP 서버 연결</div>
            <p className="node-guide-desc">MCP 서버 URL과 인증 방식을 선택해 연결하세요.</p>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">서버 이름 (선택)</div>
            <div className="input">
              <input type="text" className="input-control"
                placeholder="예: Working-On CRM"
                value={config.serverName || ''}
                onChange={e => onChange({ ...config, serverName: e.target.value })}
              />
            </div>
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">MCP 서버 URL <span className="required">*</span></div>
            <div className="input">
              <input type="text" className="input-control"
                placeholder="https://your-mcp-server.com/mcp"
                value={config.serverUrl || ''}
                onChange={e => onChange({ ...config, serverUrl: e.target.value, connected: false })}
              />
            </div>
          </div>

          {/* 인증 방식 선택 - adminPage.css .bedrock-auth-card 선택 카드 */}
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">인증 방식</div>
            <div className="bedrock-auth-choice">
              <button type="button" className={`bedrock-auth-card ${authType === 'oauth' ? 'active' : ''}`}
                onClick={() => onChange({ ...config, authType: 'oauth', apiKey: undefined })}>
                <strong>OAuth 2.0 (PKCE)</strong>
                <span>연결 시 로그인 팝업으로 인증</span>
              </button>
              <button type="button" className={`bedrock-auth-card ${authType === 'apikey' ? 'active' : ''}`}
                onClick={() => onChange({ ...config, authType: 'apikey' })}>
                <strong>API Key (Bearer)</strong>
                <span>발급받은 API Key로 인증</span>
              </button>
            </div>
          </div>

          {/* API Key 입력 (apikey 방식 선택 시) */}
          {authType === 'apikey' && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">API Key <span className="required">*</span></div>
              <div className='form-item'>
                <div className="input">
                  <input
                    type="password"
                    className="input-control"
                    placeholder="Bearer 토큰 또는 API Key 입력"
                    value={config.apiKey || ''}
                    onChange={e => onChange({ ...config, apiKey: e.target.value })}
                  />
                </div>
                <small className="form-sublabel">
                  Authorization: Bearer {'{'}API Key{'}'} 형식으로 전송됩니다
                </small>
              </div>
            </div>
          )}

          {authType === 'oauth' && (
            <Notice color="info">연결 버튼을 누르면 OAuth 로그인 팝업이 열립니다.</Notice>
          )}

          {error && <Notice color="error">{error}</Notice>}
        </div>
      )}
      {step === 1 && navPortal(
        <div className="node-step-nav">
          <Button
            color="primary"
            disabled={
              !config.serverUrl?.trim() ||
              connecting ||
              (authType === 'apikey' && !config.apiKey?.trim())
            }
            onClick={handleConnect}
          >
            {connecting ? '연결 중...' : 'MCP 서버 연결'}
          </Button>
        </div>
      )}

      {/* ── Step 2: 연결 완료 ── */}
      {step === 2 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">연결된 서버</div>
            <div className="node-account-card bedrock-guide-step">
              <div className="node-account-info">
                <span className="node-account-icon" />
                <div className="node-account-text">
                  <strong>{config.serverName || config.serverUrl}</strong>
                  <span> MCP 서버 연결됨 · {authType === 'apikey' ? 'API Key' : 'OAuth 2.0'}</span>
                </div>
              </div>
              <div className="node-account-actions">
                <Button color="gray" appearance="outline" size="sm" onClick={() => onChange({ ...config, connected: false, setupStep: 1 })}>
                  <RefreshIcon /> 재연결
                </Button>
              </div>
            </div>
            <Notice color="info">연결된 MCP 서버의 툴은 AI 채팅에서 자동으로 사용됩니다.</Notice>
          </div>

        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Dropbox 커넥터 노드 설정
// ══════════════════════════════════════════════════════════════════════════════

import dropboxLogo from '../../assets/icons/marketplace/dropbox.svg';

export interface DropboxConfig {
  operation?: 'watch_new' | 'upload';
  folderPath?: string;
  uploadFileName?: string;
  uploadFile?: File;
  // OAuth
  clientId?: string;
  clientSecret?: string;
  connected?: boolean;
  connectedEmail?: string;
  setupStep?: number;
}

const DROPBOX_STEPS = ['Dropbox 앱 만들기', 'App Key 입력', 'Dropbox 계정 연결', '작업 설정'];

const getDropboxRedirectUri = () => {
  const origin = window.location.origin;
  if (origin.includes('localhost:3003') || origin.includes('127.0.0.1:3003'))
    return origin.replace(':3003', ':3002') + '/api/auth/dropbox/callback';
  if (origin.includes('localhost:5173') || origin.includes('127.0.0.1:5173'))
    return origin.replace(':5173', ':3002') + '/api/auth/dropbox/callback';
  return origin + '/api/auth/dropbox/callback';
};
const DROPBOX_REDIRECT_URI_EXAMPLE = 'https://acxion-on.etechcloud.co.kr/api/auth/dropbox/callback';

export const getDropboxStep = (config: DropboxConfig) =>
  config.setupStep ?? (config.connected && config.clientId ? 4 : 1);

export function DropboxSteps({ config }: { config: DropboxConfig }) {
  return <NodeSetupSteps labels={DROPBOX_STEPS} step={getDropboxStep(config)} />;
}

export function DropboxConfigModal({ config, onChange }: { config: DropboxConfig; onChange: (c: DropboxConfig) => void }) {
  const step = getDropboxStep(config);
  const setStep = (s: number) => onChange({ ...config, setupStep: s });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    connectorApi.getDropboxConfig().then(saved => {
      if (saved) {
        onChange({
          ...config,
          clientId: saved.clientId,
          connected: saved.connected,
          connectedEmail: saved.connectedEmail,
          setupStep: saved.connected ? 4 : (saved.clientId ? 3 : config.setupStep ?? 1),
        });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="gmail-setup">

      {/* Step 1: Dropbox 앱 생성 가이드 */}
      {step === 1 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Dropbox 앱 만들기</div>
            <p className="node-guide-desc">Dropbox API를 사용하려면 Dropbox 개발자 콘솔에서 앱을 등록해야 합니다.</p>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">1. Dropbox 개발자 콘솔 접속</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-1</span>
              <div>
                <strong>콘솔 로그인</strong>
                <p><a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer">www.dropbox.com/developers/apps</a> 접속 후 Dropbox 계정 로그인</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">1-2</span>
              <div>
                <strong>앱 만들기 클릭</strong>
                <p>오른쪽 상단 <code className="gmail-code">Create app</code> 클릭</p>
              </div>
            </div>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">2. 앱 설정</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div>
                <strong>API 선택</strong>
                <p><code className="gmail-code">Scoped access</code> 선택</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div>
                <strong>접근 유형 선택</strong>
                <p><code className="gmail-code">Full Dropbox</code> 선택 (전체 파일 접근)</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-3</span>
              <div>
                <strong>앱 이름 입력 후 생성</strong>
                <p>앱 이름 입력 → <code className="gmail-code">Create app</code></p>
              </div>
            </div>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">3. 권한 설정</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-1</span>
              <div>
                <strong>Permissions 탭 이동</strong>
                <p>생성된 앱 → <code className="gmail-code">Permissions</code> 탭</p>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">3-2</span>
              <div>
                <strong>Files 권한 체크</strong>
                <ul className="node-scope-list">
                  <li><code className="gmail-code">files.content.read</code> 파일 읽기</li>
                  <li><code className="gmail-code">files.content.write</code> 파일 업로드</li>
                  <li><code className="gmail-code">files.metadata.read</code> 파일 목록 조회</li>
                </ul>
                <p>Submit 클릭하여 저장</p>
              </div>
            </div>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">4. 리디렉션 URI 등록</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">4-1</span>
              <div className="node-guide-step-body">
                <strong>Settings 탭 → OAuth 2 → Redirect URIs</strong>
                <p>아래 값을 복사하여 Add 클릭</p>
                <div className="bedrock-ext-row node-copy-row">
                  <input className="input-control" type="text" value={DROPBOX_REDIRECT_URI_EXAMPLE} readOnly />
                  <button type="button" className="bedrock-copy-btn node-copy-btn" title="복사"
                    onClick={() => { navigator.clipboard.writeText(DROPBOX_REDIRECT_URI_EXAMPLE); showToast('success', '리디렉션 URI 복사됨'); }}>
                    <CopyIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 1 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" onClick={() => setStep(2)}>다음</Button>
        </div>
      )}

      {/* Step 2: App Key / Secret 입력 */}
      {step === 2 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">App Key / App Secret 입력</div>
            <p className="node-guide-desc">Settings 탭에서 App key와 App secret을 복사하여 입력합니다.</p>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">App Key <span className="required">*</span></div>
            <div className="input">
              <input type="text" className="input-control" placeholder="xxxxxxxxxxxxxxxxxxxxxx"
                value={config.clientId || ''}
                onChange={(e) => onChange({ ...config, clientId: e.target.value })}
              />
            </div>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">App Secret <span className="required">*</span></div>
            <div className="input">
              <input type="password" className="input-control" placeholder="xxxxxxxxxxxxxxxxxxxxxx"
                value={config.clientSecret || ''}
                onChange={(e) => onChange({ ...config, clientSecret: e.target.value })}
              />
            </div>
            {saveError && <div className="node-save-error">{saveError}</div>}
          </div>
        </div>
      )}
      {step === 2 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(1)}>이전</Button>
          <Button color="primary" disabled={!config.clientId || !config.clientSecret || saving}
            onClick={async () => {
              setSaving(true); setSaveError('');
              try {
                await connectorApi.saveDropboxConfig(config.clientId!, config.clientSecret!);
                setStep(3);
              } catch { setSaveError('저장 실패. 다시 시도해주세요.'); }
              finally { setSaving(false); }
            }}>
            {saving ? '저장 중...' : '저장 후 다음'}
          </Button>
        </div>
      )}

      {/* Step 3: Dropbox 계정 연결 */}
      {step === 3 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section node-guide-head">
            <div className="bedrock-guide-title">Dropbox 계정 연결</div>
            <p className="node-guide-desc">아래 버튼으로 Dropbox에 로그인하여 권한을 허용합니다.</p>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">허용되는 권한</div>
            <div className="node-permission-list">
              <div className="node-permission-item"><PlusIcon />새 파일 추가 감지</div>
              <div className="node-permission-item"><UploadIcon />파일 업로드</div>
            </div>
          </div>
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">Dropbox 계정 연결</div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-1</span>
              <div className="node-guide-step-body">
                <strong>Dropbox 로그인</strong>
                <button type="button" className="btn-google node-oauth-btn"
                  style={{ background: '#0061FF', color: '#fff', border: 'none' }}
                  onClick={async () => {
                    try {
                      const url = await connectorApi.getDropboxAuthUrl();
                      const popup = window.open(url, 'dropbox-auth', 'width=500,height=700');
                      const handler = (e: MessageEvent) => {
                        if (e.data?.type === 'DROPBOX_CONNECTED') {
                          window.removeEventListener('message', handler);
                          popup?.close();
                          onChange({ ...config, connected: true, connectedEmail: e.data.email, setupStep: 4 });
                        }
                      };
                      window.addEventListener('message', handler);
                    } catch {
                      alert('인증 URL을 가져오는데 실패했습니다. App Key/Secret을 확인하세요.');
                    }
                  }}>
                  <img src={dropboxLogo} alt="" style={{ width: 16, height: 16, marginRight: 8 }} />
                  Dropbox 계정으로 연결
                </button>
              </div>
            </div>
            <div className="bedrock-guide-step">
              <span className="bedrock-guide-badge">2-2</span>
              <div><strong>연결 확인</strong><p>로그인 완료 후 연결 확인 버튼을 눌러주세요</p></div>
            </div>
          </div>
        </div>
      )}
      {step === 3 && navPortal(
        <div className="node-step-nav">
          <Button color="primary" appearance="outline" onClick={() => setStep(2)}>이전</Button>
          <Button color="primary" onClick={async () => {
            try {
              const saved = await connectorApi.getDropboxConfig();
              if (saved?.connected) {
                onChange({ ...config, connected: true, connectedEmail: saved.connectedEmail, setupStep: 4 });
              } else {
                alert('아직 연결되지 않았습니다. Dropbox 계정 연결 버튼을 먼저 클릭해주세요.');
              }
            } catch { alert('연결 상태 확인 실패. 다시 시도해주세요.'); }
          }}>
            연결 확인
          </Button>
        </div>
      )}

      {/* Step 4: 작업 설정 */}
      {step === 4 && (
        <div className="bedrock-guide">
          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">연결된 계정</div>
            {config.connectedEmail && (
              <div className="node-account-card bedrock-guide-step">
                <div className="node-account-info">
                  <img src={dropboxLogo} alt="" style={{ width: 22, height: 22, marginRight: 8, flexShrink: 0 }} />
                  <div className="node-account-text">
                    <strong>{config.connectedEmail}</strong>
                    <span> Dropbox 계정이 연결되어 있습니다</span>
                  </div>
                </div>
                <div className="node-account-actions">
                  <Button color="gray" appearance="outline" size="sm" onClick={async () => {
                    await connectorApi.disconnectDropbox().catch(() => {});
                    onChange({ ...config, connected: false, connectedEmail: undefined, setupStep: 3 });
                  }}>
                    <RefreshIcon /> 계정 재연결
                  </Button>
                  <Button color="gray" appearance="outline" size="sm" onClick={() => setStep(2)}>
                    <SettingIcon /> App Key 재설정
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="bedrock-guide-section">
            <div className="bedrock-guide-title">작업 유형</div>
            <Select value={config.operation || 'watch_new'}
              onChange={(e) => onChange({ ...config, operation: e.target.value as DropboxConfig['operation'] })}>
              <optgroup label="감지">
                <option value="watch_new">새 파일 감지</option>
              </optgroup>
              <optgroup label="쓰기">
                <option value="upload">파일 업로드</option>
              </optgroup>
            </Select>
          </div>

          {(config.operation === 'watch_new' || !config.operation) && (
            <div className="bedrock-guide-section">
              <div className="bedrock-guide-title">폴더 경로 (선택)</div>
              <div className="input">
                <input type="text" className="input-control" placeholder="/폴더명 또는 비워두면 전체 드라이브"
                  value={config.folderPath || ''}
                  onChange={(e) => onChange({ ...config, folderPath: e.target.value })}
                />
              </div>
              <small className="form-sublabel">
                스케줄 트리거와 연결하면 주기마다 폴더를 확인하여 <strong>새로 추가된 파일</strong>을 다음 노드로 전달합니다.
              </small>
            </div>
          )}

          {config.operation === 'upload' && (
            <>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">업로드할 파일</div>
                <div className="pipe-file-dropzone drive-upload-dropzone"
                  onClick={() => { (document.getElementById('dropbox-upload-input') as HTMLInputElement)?.click(); }}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.currentTarget.classList.remove('dragover');
                    const file = e.dataTransfer.files[0];
                    if (file) onChange({ ...config, uploadFileName: file.name, uploadFile: file });
                  }}>
                  <div className="pipe-file-dropzone-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                      <polyline points="13 2 13 9 20 9" />
                    </svg>
                  </div>
                  <div className="pipe-file-dropzone-text">파일을 드래그하거나 클릭해서 선택</div>
                  <div className="pipe-file-dropzone-sub">모든 파일 형식 지원 (최대 150MB)</div>
                  <input id="dropbox-upload-input" type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onChange({ ...config, uploadFileName: file.name, uploadFile: file });
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                </div>
                {config.uploadFileName && (
                  <div className="file-item pipe-file-item drive-upload-file-item">
                    <span className="pipe-file-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                        <polyline points="13 2 13 9 20 9" />
                      </svg>
                    </span>
                    <div className="pipe-file-info">
                      <div className="pipe-file-name">{config.uploadFileName}</div>
                      {config.uploadFile && (
                        <div className="pipe-file-meta">
                          {config.uploadFile.size < 1024 * 1024
                            ? `${(config.uploadFile.size / 1024).toFixed(1)} KB`
                            : `${(config.uploadFile.size / (1024 * 1024)).toFixed(1)} MB`}
                        </div>
                      )}
                    </div>
                    <Badge color="success" dot>준비됨</Badge>
                    <button className="pipe-file-remove" onClick={() => onChange({ ...config, uploadFileName: undefined, uploadFile: undefined })}>✕</button>
                  </div>
                )}
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">저장할 폴더 경로 (선택)</div>
                <div className="form-item">
                  <div className="input">
                    <input type="text" className="input-control" placeholder="/폴더명 (비우면 루트에 저장)"
                      value={config.folderPath || ''}
                      onChange={(e) => onChange({ ...config, folderPath: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export type {
  OutputNodeConfig,
  NotifyNodeConfig,
  MailTriggerConfig,
  ScheduleTriggerConfig,
  WebhookTriggerConfig,
  HttpCallConfig,
  EmailSendConfig,
  GoogleDriveConfig,
  SlackConfig,
  JiraConfig,
  GmailConfig,
  EmailConnectorNodeConfig,
  NotionConfig,
  NaverMailConfig,
  WebSearchConfig,
  McpConfig,
};
